import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '../components/AppShell';
import PaymentModal from '../components/PaymentModal';
import RequestShowingModal from '../components/RequestShowingModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useAuth } from '../context/AuthContext';
import { mapL2ToCardShape } from '../utils/listingShape';
import HomesLockedPage, { HOMES_LOCKED_STATUSES } from '../components/HomesLockedPage';
import { SHOWING_OFFER_BLOCKED_STAGES } from '../constants/statusColors';
import HomesMap from '../components/HomesMap';
import { LICENSED_STATE_CODES, licensedStatesShort } from '../constants/marketExpansion';
// 🔑 ONE SLOT GRID FOR BOTH SHOWING FORMS. This page used to offer periods —
// morning / afternoon / evening / flexible — into the same column the Showami
// modal fills with a validated "HH:MM". Importing the same list, the same 24-hour
// floor and the same combiner is what keeps the two from disagreeing again.
import { TIME_SLOTS, isTimeSlotDisabled, earliestValidDateStr, combineDateAndTime }
  from '../utils/showingDateUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const RECENTLY_VIEWED_KEY = 'doorly_recently_viewed';

// Sticky search/filter state — persisted in sessionStorage so navigating
// to a listing detail and back restores the borrower's filtered view
// instead of resetting to the broad listing set. sessionStorage (not
// localStorage) so stale filters don't linger across browser sessions.
// Shape: { aiQuery, aiParsed, aiUnmapped, aiActive, filters }
const HOMES_FILTER_STATE_KEY = 'doorly_homes_filter_state';

function loadStickyFilterState() {
  try {
    const raw = sessionStorage.getItem(HOMES_FILTER_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) { return null; }
}

function saveStickyFilterState(patch) {
  try {
    const prev = loadStickyFilterState() || {};
    sessionStorage.setItem(HOMES_FILTER_STATE_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch (_) {}
}

// ── Formatters ────────────────────────────────────────────────────────────────
function fmt$(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

// ── Safe address builder ──────────────────────────────────────────────────────
// Previously five sites in this file built the canonical address string
// via `${street}, ${city}, ${state} ${zip}` templates. Two failure modes
// surfaced in production rows on offer_requests.address:
//   1) Doubled tail: "3814 Desert Willow Way, Katy, TX 77493, Katy, TX, 77493"
//      — happens when `street` already holds the complete address (user
//      typed it whole into the AddressChecker input AND city/state/zip
//      ALSO got populated by Google autocomplete on a partial match).
//      The naive template then appends the city/state/zip a second time.
//   2) Empty-segment commas: "623 Eagles Way, , TX, " when city/zip are
//      empty strings — orphan commas + extra spaces that downstream
//      parseAddress / HouseCanary calls have to clean up.
// This helper guards both: if `street` already contains a 5-digit ZIP it's
// treated as a complete address and returned as-is; otherwise the parts
// are joined cleanly with state+zip combined into a single segment (no
// comma between them, matching standard US address punctuation).
function buildFullAddress(street, city, state, zip) {
  if (!street) return '';
  const s = String(street).trim();
  // Already-complete address — has a ZIP in it. Trust it as-is.
  if (/\b\d{5}\b/.test(s)) return s;
  return [
    s,
    city,
    [state, zip].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
}

// ── Status badge config ───────────────────────────────────────────────────────
// Active uses the Doorly brand-green pill (#acf44e bg + #111827 text —
// the brand combo also used by ReviewModule's Accept buttons). Pending
// and Coming Soon keep the Tailwind class shorthand because they
// inherit white text and tonal hue is fine for those cases.
const STATUS_CONFIG = {
  Active:        { style: { background: '#acf44e', color: '#111827' }, label: 'Active' },
  Pending:       { bg: 'bg-yellow-500',                                 label: 'Pending' },
  'Coming Soon': { bg: 'bg-blue-500',                                   label: 'Coming Soon' },
};

// LockedPage extracted to components/HomesLockedPage.jsx (3b commit 4)
// so HomeDetail.js can render the same gate visual. Imported as
// HomesLockedPage above; HOMES_LOCKED_STATUSES (the stage list) is
// also imported from there so both pages bind to one definition.

// ── "Can't find a property?" Modal ───────────────────────────────────────────
// Removable filter pill — used to render parsed AI-search filters
// under the search bar. The × button calls onRemove which re-runs
// the AI search with the remaining filters (see removeAiFilter in
// the Homes component).
function AiFilterTag({ label, onRemove }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
      style={{ background: '#eff3fe', color: '#3a6ad9', border: '1px solid #c7d8f8' }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-100"
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}

function GuardrailCard({ title, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${open ? 'border-[#3a6ad9]' : 'border-gray-100'} shadow-sm`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-45' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[600px]' : 'max-h-0'}`}>
        <div className={`px-5 pb-5 border-t border-gray-100 pt-4 ${open ? 'border-l-4' : ''}`} style={open ? { borderLeftColor: '#3a6ad9' } : {}}>
          {children}
        </div>
      </div>
    </div>
  );
}

function AddressChecker({ token, onOfferRequest, onWaitlist }) {
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!address.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/homes/check-eligibility`,
        { address },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
    } catch (_) {
      setResult({ result: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleCheck} className="flex gap-3">
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Main St, Austin, TX 78701"
          className="flex-1 h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3a6ad9]/30"
        />
        <button
          type="submit"
          disabled={loading || !address.trim()}
          className="btn-primary text-white disabled:opacity-60"
          style={{ backgroundColor: '#3a6ad9' }}
        >
          {loading ? (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <>
              <span>Check</span>
              <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
            </>
          )}
        </button>
      </form>

      {result && (
        <div className={`rounded-xl p-4 border ${
          result.result === 'eligible' ? 'bg-green-50 border-green-200' :
          result.result === 'unavailable' ? 'bg-yellow-50 border-yellow-200' :
          'bg-blue-50 border-blue-200'
        }`}>
          {result.result === 'eligible' && (
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-800">This home may be eligible!</p>
                <p className="text-sm text-green-700 mt-0.5">Submit a request and our team will review this property for you.</p>
                <button
                  onClick={() => onOfferRequest({ id: 'custom', address: { street: address, city: '', state: result.detected_state || '', zip: '' }, price: null, photos: [] })}
                  className="mt-3 btn-primary text-white text-xs"
                  style={{ backgroundColor: '#acf44e' }}
                >
                  <span>Start Purchase</span>
                  <span className="btn-arrow" style={{ backgroundColor: '#fff' }}><svg viewBox="0 0 24 24" fill="none" stroke="#acf44e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
                </button>
              </div>
            </div>
          )}
          {result.result === 'unavailable' && (
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-800">Doorly isn't in {result.detected_state} yet</p>
                {/* 🔑 Read from the licence table, never typed (Jesse, 2026-10-29: Texas only; this used to name a second state). */}
                <p className="text-sm text-yellow-700 mt-0.5">We currently serve {licensedStatesShort()} only. Join our waitlist to be first when we expand.</p>
                <button onClick={onWaitlist} className="mt-3 btn-primary text-black text-xs" style={{ backgroundColor: '#fffd5b' }}>
                  <span>Join Waitlist</span>
                  <span className="btn-arrow" style={{ backgroundColor: '#000' }}><svg viewBox="0 0 24 24" fill="none" stroke="#fffd5b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
                </button>
              </div>
            </div>
          )}
          {(result.result === 'unknown' || result.result === 'error') && (
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-800">We'll review this address manually</p>
                <p className="text-sm text-blue-700 mt-0.5">Submit this address for review — our team will reach out within 1 business day.</p>
                <button
                  onClick={() => onOfferRequest({ id: 'custom', address: { street: address, city: '', state: '', zip: '' }, price: null, photos: [] })}
                  className="mt-3 btn-primary text-white text-xs"
                  style={{ backgroundColor: '#3a6ad9' }}
                >
                  <span>Submit for Review</span>
                  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SmartModal({ onClose, token, onOfferRequest, maxBuyingPower, resetFilters, setActiveTab, hasActiveOffer }) {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* fixed, not absolute: an absolute backdrop stops at the overlay's safe-area border (index.css .safe-modal-overlay) */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="safe-modal-box relative w-full max-w-lg bg-gray-50 rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ '--sm-max': '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-black">Can't find a specific property?</h2>
            <p className="text-xs text-gray-400 mt-0.5">Here are some common reasons and solutions.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {/* Manually Request Offer — primary CTA above the FAQ stack. A
              temporary bridge until all MLS feeds are live: opens the
              standard offer flow (OfferRequestModule) in manual mode, where
              Step 1 lets the borrower type the property address + list price
              for any home that isn't in the listings yet. Hidden once the
              borrower has an active offer (offer_requested or later, per
              hasActiveOffer) — they can't request another property until the
              current one resolves, matching the backend stage gate. */}
          {!hasActiveOffer && (
          <div className="bg-[#eff3fe] border border-[#c7d8f8] rounded-2xl p-4">
            <p className="text-sm font-semibold text-gray-800">Found a home that isn't listed here?</p>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">Enter the property address and list price to request a Doorly offer on it directly.</p>
            <button
              onClick={() => { onClose(); onOfferRequest({ manual: true }); }}
              className="w-full inline-flex items-center justify-center gap-2 btn-primary text-white text-sm font-bold"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              <span>Manually Start Purchase</span>
            </button>
          </div>
          )}

          <GuardrailCard title="Home is not listed or already under contract">
            <div className="space-y-3 text-sm text-gray-600">
              <p className="font-medium text-gray-800">This home may not appear because:</p>
              <ul className="space-y-1 ml-4">
                {['It is not currently listed for sale on the MLS', 'It is already under contract or pending', 'It was recently sold'].map((item) => (
                  <li key={item} className="flex items-start gap-2"><span className="text-[#3a6ad9] mt-0.5">•</span> {item}</li>
                ))}
              </ul>
              <p className="font-medium text-gray-800 pt-1">What you can do:</p>
              <ul className="space-y-1 ml-4">
                {['Double-check the listing status', 'Search for similar homes in the same area'].map((item) => (
                  <li key={item} className="flex items-start gap-2"><span className="text-[#3a6ad9] mt-0.5">•</span> {item}</li>
                ))}
              </ul>
              <button
                onClick={() => { onClose(); setActiveTab('all'); resetFilters(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="mt-2 btn-primary text-white text-xs"
                style={{ backgroundColor: '#3a6ad9' }}
              >
                <span>Search Similar Homes</span>
                <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
              </button>
            </div>
          </GuardrailCard>

          <GuardrailCard title="Home is above your approved purchase range">
            <div className="space-y-3 text-sm text-gray-600">
              <p>This home may be outside your current approval amount.</p>
              {maxBuyingPower && (
                <div className="bg-[#eff3fe] rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-600">Your current max purchase power</span>
                  <span className="font-bold text-[#3a6ad9]">{fmt$(maxBuyingPower)}</span>
                </div>
              )}
              <p className="font-medium text-gray-800 pt-1">Possible options:</p>
              <ul className="space-y-1 ml-4">
                {['Increase your down payment', 'Add a co-borrower', 'Wait and reapply after improving financial profile'].map((item) => (
                  <li key={item} className="flex items-start gap-2"><span className="text-[#3a6ad9] mt-0.5">•</span> {item}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  onClick={() => { onClose(); resetFilters(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="btn-primary text-white text-xs"
                  style={{ backgroundColor: '#3a6ad9' }}
                >
                  <span>View Homes in Your Range</span>
                  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
                </button>
                <button onClick={() => { onClose(); navigate('/documents'); }} className="btn-secondary border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs">
                  Edit Application to Update Buying Power
                </button>
              </div>
            </div>
          </GuardrailCard>

          <GuardrailCard title="Home does not meet Doorly eligibility criteria">
            <div className="space-y-3 text-sm text-gray-600">
              <p className="font-medium text-gray-800">This property may not qualify due to:</p>
              <ul className="space-y-1 ml-4">
                {[
                  'Property condition or livability issues',
                  'Property type restrictions (multi-family, land, etc.)',
                  'Title or legal concerns',
                  "Location outside of Doorly's current service areas",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2"><span className="text-[#3a6ad9] mt-0.5">•</span> {item}</li>
                ))}
              </ul>
              <button data-guide-cta="Browse Eligible Homes"
                onClick={() => { onClose(); resetFilters(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="btn-primary text-white text-xs"
                style={{ backgroundColor: '#3a6ad9' }}
              >
                <span>Browse Eligible Homes</span>
                <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
              </button>
            </div>
          </GuardrailCard>

          <GuardrailCard title="Looking for a specific address? Check eligibility" defaultOpen={true}>
            <p className="text-sm text-gray-500 mb-4">Enter an address to check if it's eligible for a Doorly offer.</p>
            <AddressChecker
              token={token}
              onOfferRequest={(l) => { onClose(); onOfferRequest(l); }}
              onWaitlist={() => window.open('/pre-qualify', '_blank')}
            />
          </GuardrailCard>
        </div>
      </div>
    </div>
  );
}

// ── Listing Card ──────────────────────────────────────────────────────────────
function ListingCard({ listing, mlsProfiles = [], saved, onToggleSave, onShowingRequest, onOfferRequest, hasActiveOffer, referredByRealtor }) {
  const navigate = useNavigate();
  const status = STATUS_CONFIG[listing.status] || STATUS_CONFIG['Active'];
  // Per-card MLS attribution (compliance). Resolve this listing's originating
  // MLS profile by listing.source (= OriginatingSystemName) against the
  // already-fetched mls-attribution profiles. Manual/off-MLS entries have no
  // source → no footer.
  const mlsProfile = (mlsProfiles || []).find(
    (p) => (p.originatingSystemName || '').toLowerCase() === String(listing.source || '').toLowerCase()
  ) || null;

  const handleCardClick = () => {
    try {
      const existing = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      const filtered = existing.filter((id) => id !== listing.id);
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify([listing.id, ...filtered].slice(0, 10)));
    } catch (_) {}
    navigate(`/homes/${listing.id}`);
  };

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-all duration-150 flex flex-col cursor-pointer hover:-translate-y-0.5"
      onClick={handleCardClick}
    >
      {/* Photo — Layer 2 listings have an array of photo URL strings
          (mapped from {url, order, ...} by mapL2ToCardShape); legacy
          MOCK listings have the same string-array shape. When the
          array is empty (rare in HAR; gated by Layer 1's PhotosCount
          gt 0 floor but defensive here for any edge cases), render a
          neutral Doorly-mark placeholder rather than a broken <img>. */}
      <div className="relative" style={{ paddingBottom: '56.25%' }}>
        {listing.photos && listing.photos[0] ? (
          <img
            src={listing.photos[0]}
            alt={listing.address.street || listing.address.full || 'Listing'}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-xs font-semibold text-gray-400">No photo available</span>
          </div>
        )}
        {/* Status badge — Active uses inline Doorly-green style; other
            statuses still use Tailwind class shorthand from STATUS_CONFIG. */}
        {status.style ? (
          <span
            className="absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full"
            style={status.style}
          >
            {status.label}
          </span>
        ) : (
          <span className={`absolute top-3 left-3 ${status.bg} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
            {status.label}
          </span>
        )}
        {/* Promoted badge */}
        {listing.is_promoted && (
          <span className="absolute top-3 right-10 bg-[#fffd5b] text-black text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Featured
          </span>
        )}
        {/* Save heart */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSave(listing); }}
          className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
        >
          <svg className="w-4 h-4" fill={saved ? '#ff082f' : 'none'} stroke={saved ? '#ff082f' : '#9ca3af'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="font-bold text-black text-xl leading-tight">{fmt$(listing.price)}</p>
        <p className="text-gray-800 text-sm font-medium mt-0.5">{listing.address.street}</p>
        <p className="text-gray-400 text-xs">{listing.address.city}, {listing.address.state} {listing.address.zip}</p>

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="font-medium">{listing.beds}</span> bd
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            <span className="font-medium">{listing.baths}</span> ba
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            <span className="font-medium">{Number(listing.sqft || 0).toLocaleString()}</span> sqft
          </span>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">{listing.property_type} · {listing.year_built}</span>
          {listing.days_on_market > 0 && (
            <span className="text-xs text-gray-400">{listing.days_on_market}d on market</span>
          )}
        </div>

        {/* SmartEstimate — Layer 2 overlay. Computed server-side from
            the borrower's atlas_final_rate + accepted_down_pct +
            listing.price. NO PMI (AFM doesn't bind MI). NO hardcoded
            rate. Renders null state when the borrower hasn't accepted
            a SmartEstimate option yet (atlas_final_rate not yet set);
            the prompt to complete pricing lives elsewhere on the
            Dashboard hero. */}
        {listing.smartEstimate?.monthlyPayment ? (
          <div className="mt-3 pt-2 border-t border-gray-100">
            <div className="flex items-baseline gap-2">
              {/* Doorly blue for the monthly payment — matches the Buying
                  Power badge + the primary CTA palette so the borrower's
                  eye recognizes "this is a Doorly-computed number." */}
              <span className="text-sm font-bold" style={{ color: '#3a6ad9' }}>
                {fmt$(listing.smartEstimate.monthlyPayment)}/mo
              </span>
              <span className="text-xs text-gray-400">est.</span>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {listing.smartEstimate.downPct}% down · {listing.smartEstimate.rate}% rate · PITI{listing.smartEstimate.breakdown?.hoa > 0 ? ' + HOA' : ''}
            </div>
            {/* Cash to close — borrower's true wire amount at contract
                execution. Backend computeSmartEstimate folds DP +
                closing costs (5.5%) + program fee (2% on Option 1, $0
                on Option 2) into this figure using the resale-adjusted
                borrower price, not the raw list price. */}
            {listing.smartEstimate.cashToClose ? (
              <div className="text-xs text-gray-500 mt-0.5">
                Est. cash to close: <span style={{ fontWeight: 600, color: '#374151' }}>{fmt$(listing.smartEstimate.cashToClose)}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* UW annotation — Layer 2 overlay. Surfaces per-subtype notes
            from the UW guide (Condo → HOA / warrantability review
            required; 2–4 unit properties are not eligible under AFM v2). Amber chip per brand: #ffdd86 background +
            #111827 text — the only legible combination of the two. */}
        {listing.uwAnnotation ? (
          <div className="mt-2" style={{
            background: '#ffdd86', color: '#111827',
            padding: '6px 10px', borderRadius: 6,
            fontSize: 11, lineHeight: 1.4, fontWeight: 600,
          }}>
            {listing.uwAnnotation}
          </div>
        ) : null}

        {/* Action buttons — stage-based per the 3c follow-up.
            · home_shopping → 3 buttons: Schedule Showing, Request
              Offer, Save to Favorites (latter swaps to 'Saved to
              Favorites' + filled-heart when saved).
            · post-home_shopping (hasActiveOffer per
              SHOWING_OFFER_BLOCKED_STAGES) → single 'View Listing'
              button. Showing/offer CTAs stay hidden (no new offers
              while one is in flight) but the borrower can still
              browse and inspect the listing detail page. */}
        {hasActiveOffer ? (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <a
              href={`/homes/${listing.id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-center gap-1.5 text-xs font-semibold rounded-xl transition-colors"
              style={{
                background:     '#3a6ad9',
                color:          '#ffffff',
                padding:        '12px 16px',
                minHeight:      '44px',
                textDecoration: 'none',
              }}
            >
              View Listing →
            </a>
          </div>
        ) : (
          <div className="mt-4 pt-3 border-t border-gray-100" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* 🚨 A REALTOR-REFERRED BORROWER ALREADY HAS SOMEBODY TO SHOW THEM
                HOUSES, and that person is owed the work. Doorly booking a
                third-party agent through Showami would be arranging a showing
                around the realtor who introduced the buyer — on a file where that
                realtor's compensation turns on having performed the §2.1
                property-identification and showing services.
                ⚠️ referredByRealtor comes from utils/borrowerArrival.js via
                /api/users/me/loan-details, NOT derived on this page. It defaults
                FALSE when unknown, so a failed lookup leaves the button where it
                was rather than removing a feature on a query error. */}
            {referredByRealtor ? (
              <div style={{
                flex: '1 1 calc(50% - 4px)', minHeight: '44px', display: 'flex',
                alignItems: 'center', background: '#f3f4f6', borderRadius: 12,
                padding: '8px 12px', fontSize: 11, lineHeight: 1.4, color: '#4b5563',
              }}>
                Your agent arranges showings — message them about this one, then
                start the purchase here when you are ready.
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onShowingRequest(listing); }}
                className="text-xs font-semibold rounded-xl transition-colors"
                style={{
                  flex:        '1 1 calc(50% - 4px)',
                  background:  '#2563EB',
                  color:       '#ffffff',
                  border:      'none',
                  padding:     '12px 12px',
                  minHeight:   '44px',
                }}
              >
                Schedule Showing
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onOfferRequest(listing); }}
              className="text-xs font-bold rounded-xl transition-colors"
              style={{
                flex:        '1 1 calc(50% - 4px)',
                background:  '#fffd5b',
                color:       '#1a1a1a',
                border:      'none',
                padding:     '12px 12px',
                minHeight:   '44px',
              }}
            >
              Start Purchase
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSave(listing); }}
              className="text-xs font-semibold rounded-xl transition-colors"
              style={{
                flex:           '1 1 100%',
                background:     saved ? '#fff0f3' : '#ffffff',
                color:          saved ? '#ff082f' : '#374151',
                border:         `1px solid ${saved ? '#fecdd3' : '#e5e7eb'}`,
                padding:        '10px 12px',
                minHeight:      '44px',
                display:        'inline-flex',
                alignItems:     'center',
                justifyContent: 'center',
                gap:            '8px',   // proper icon ↔ text spacing
              }}
            >
              <svg
                width="14" height="14" viewBox="0 0 24 24"
                fill={saved ? 'currentColor' : 'none'}
                stroke="currentColor" strokeWidth="2"
                style={{ flexShrink: 0 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span>{saved ? 'Saved to Favorites' : 'Save to Favorites'}</span>
            </button>
          </div>
        )}

        {/* MLS attribution — compliance. Per-card logo (or "courtesy of"
            fallback) + truncated disclaimer for the listing's originating
            MLS. Renders nothing for manual/off-MLS entries (no source) or
            when the attribution endpoint is unavailable. */}
        {mlsProfile && (mlsProfile.logoUrl || mlsProfile.displayName) && (
          <div className="mt-3 pt-2" style={{ borderTop: '1px solid #f3f4f6' }}>
            {mlsProfile.logoUrl ? (
              <img
                src={mlsProfile.logoUrl}
                alt={mlsProfile.displayName || mlsProfile.originatingSystemName || 'MLS'}
                style={{ maxWidth: 80, maxHeight: 30, width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <div className="text-xs text-gray-400">Listing courtesy of {mlsProfile.displayName}</div>
            )}
            {mlsProfile.disclaimer && (
              <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 4, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {mlsProfile.disclaimer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Offer Request Module — 4-step wizard (property → loan terms → updated
//    estimate → confirm & pay). Replaces the old single-screen OfferModal.
//    Used for BOTH MLS-listing offers (prefilled from the listing) and manual
//    off-MLS offers (listing.manual = true). `feeWaived` skips the $500
//    PaymentModal at Step 4; otherwise Step 4 hands a normalized payload to
//    the parent, which opens the existing PaymentModal. Submit body shape is
//    unchanged: POST /api/homes/offer.
const ORM = {
  stepHeader: { fontSize: 19, fontWeight: 800, color: '#111827', marginBottom: 6 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '14px 0 6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, color: '#111827', boxSizing: 'border-box', outline: 'none' },
  hint: { fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.5 },
  sub: { fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: '6px 0 16px' },
  optionPill: { display: 'inline-block', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 999, padding: '5px 14px', fontSize: 12, fontWeight: 700, marginBottom: 14 },
  termRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' },
  termLabel: { fontSize: 13, color: '#6b7280' },
  termValue: { fontSize: 14, fontWeight: 700, color: '#111827' },
  infoBox: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: '#4b5563', lineHeight: 1.6, marginTop: 16 },
  navRow: { display: 'flex', gap: 10, marginTop: 22 },
  backBtn: { flex: '0 0 auto', padding: '12px 18px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  primaryBtn: { flex: 1, padding: '13px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer' },
};

// Full PITI monthly payment estimate for a given purchase price.
// Mirrors the Atlas pricing engine (backend/utils/atlas.js): 30yr P&I +
// property tax (1.8%/yr) + homeowners insurance (0.5%/yr). HOA is $0 by
// default, same as Atlas. PMI is no longer included — non-QM loans
// can't bind MI. Returns a rounded dollar figure or null.
const ATLAS_TAX_RATE = 0.018;   // annual property tax assumption
const ATLAS_INS_RATE = 0.005;   // annual homeowners insurance assumption
function estFullMonthlyPayment({ price, downPct, rate /* fico kept for callsite stability */ }) {
  const p = Number(price) || 0;
  const r = Number(rate);
  if (p <= 0 || !r) return null;
  const downAmt = Math.round(p * ((Number(downPct) || 0) / 100));
  const loanAmt = Math.max(0, p - downAmt);
  const monthlyRate = r / 100 / 12;
  const n = 360; // 30-year term
  const pi = monthlyRate > 0
    ? loanAmt * monthlyRate * Math.pow(1 + monthlyRate, n) / (Math.pow(1 + monthlyRate, n) - 1)
    : 0;
  const taxes = p * ATLAS_TAX_RATE / 12;
  const insurance = p * ATLAS_INS_RATE / 12;
  return Math.round(pi + taxes + insurance);
}

// Exported so the HomeDetail page can mount the same 3-step wizard on
// its own Request Offer button — keeps the offer flow consistent
// regardless of whether the borrower entered from a card or the
// expanded listing view.
export function OfferRequestModule({ listing, loanDetails, feeWaived, onClose, onWaivedSubmit, onPaymentRequest }) {
  const isManual = !!listing?.manual;
  const prefillAddress = listing
    ? (isManual
        ? (listing.address?.street || (typeof listing.address === 'string' ? listing.address : '') || '')
        : buildFullAddress(listing.address?.street, listing.address?.city, listing.address?.state, listing.address?.zip))
    : '';
  const prefillPrice = (listing?.price != null && listing?.price !== '') ? String(listing.price) : '';

  const [step, setStep] = useState(1);
  const [propertyAddress, setPropertyAddress] = useState(prefillAddress);
  const [listPrice, setListPrice] = useState(prefillPrice);
  const [propertyNotes, setPropertyNotes] = useState(listing?.notes || '');
  const [acks, setAcks] = useState({
    negotiation: false, downPayment: false, closingFunds: false,
    paymentEstimate: false, addressConfirm: false,
  });

  // Render as a full-screen sheet on phones (centered card on desktop). A
  // centered, maxHeight:90vh card gets clipped by mobile browser chrome and
  // covered by the on-screen keyboard when an input is focused — a full-bleed
  // sheet that scrolls internally avoids both.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  // Terms used by Step 1 (over-budget guard) + Step 3 (down-payment ack).
  // The Loan Comparison step (Step 2) computes its own figures inline.
  // 🔑 THE SHOPPING CAP IS THE DECISION MAXIMUM (Jesse, 2026-11-01), not the signed price — the same figure the server
  // enforces (backend/utils/shoppingCap). 🚨 Never max_approval_amount (fp-smartest, 2026-11-01): accept-terms writes the
  // price SIGNED there, so a borrower who signed below the maximum was blocked from homes they may buy. Until loan-details
  // answers there is no client cap (the server's still applies).
  const maxPrice = loanDetails?.decision_max_purchase_price || null;
  const downPct = loanDetails?.down_payment_pct || 10;
  const lp = Number(listPrice) || 0;
  const overBudget = maxPrice ? lp > Number(maxPrice) : false;
  // Borrower's actual purchase price = list price + resale adjustment.
  // The Step 3 down-payment ack and any other top-level down-amount
  // consumer must reference borrowerPrice, not lp — % of list price is
  // Doorly's purchase basis, not the borrower's, and undershoots the
  // wire amount the borrower will see at contract execution.
  const borrowerPriceForAck = lp > 0
    ? Math.round(lp)   // D3: no resale adjustment
    : 0;
  const downAmt = Math.round(borrowerPriceForAck * (downPct / 100));

  const allAcked = Object.values(acks).every(Boolean);

  const buildPayload = () => ({
    listing_id: listing?.id || ('manual_' + Date.now()),
    address: propertyAddress.trim(),
    list_price: lp || null,
    notes: propertyNotes.trim() || null,
    listing_url: listing?.listing_url || null,
    manual_entry: isManual,
    source: isManual ? 'borrower_manual' : undefined,
  });

  const stepTitle = ['Property Details', 'Loan Comparison', 'Confirm & Submit'][step - 1];

  return (
    <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}>
      <div className="safe-modal-box" style={{ width: '100%', maxWidth: isMobile ? '100%' : 540, height: isMobile ? '100%' : 'auto', '--sm-max': isMobile ? '100%' : '90vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: isMobile ? 0 : 16, boxShadow: isMobile ? 'none' : '0 25px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        {/* Solid blue header with yellow step accent */}
        <div style={{ background: '#3a6ad9', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#fffd5b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>Step {step} of 3</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{stepTitle}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>✕</button>
        </div>
        {/* Progress bar */}
        <div style={{ height: 3, background: '#e5e7eb' }}>
          <div style={{ height: '100%', width: `${(step / 3) * 100}%`, background: '#fffd5b', transition: 'width 300ms ease' }} />
        </div>

        <div style={{ padding: isMobile ? '18px 16px' : 24, overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1, minHeight: 0 }}>
          {/* ── Step 1 — Property ── */}
          {step === 1 && (
            <div>
              <div style={ORM.stepHeader}>Tell us about the property</div>
              <label style={ORM.label}>Property Address</label>
              <AddressAutocomplete
                name="offer_property_address"
                value={propertyAddress}
                placeholder="123 Main St, Austin, TX 78745"
                onChange={e => setPropertyAddress(e.target.value)}
                onSelect={({ street, city, state, zip }) =>
                  setPropertyAddress(buildFullAddress(street, city, state, zip) || propertyAddress)
                }
                className="offer-addr-input"
                autoComplete="street-address"
              />
              <style>{`
                .offer-addr-input {
                  width: 100%;
                  padding: 10px 12px 10px 36px;
                  border: 1px solid #e5e7eb;
                  border-radius: 10px;
                  font-size: 14px;
                  color: #111827;
                  font-family: inherit;
                  box-sizing: border-box;
                  outline: none;
                }
                .offer-addr-input:focus { border-color: #3a6ad9; }
              `}</style>
              <label style={ORM.label}>List Price</label>
              {(() => {
                // Editable only on borrower-typed manual entries (off-MLS).
                // For any MLS-sourced listing, the list price is fixed by
                // the feed — let the borrower see it but not edit it, so
                // the loan-comparison math on Step 2 always reflects the
                // listing's actual list price.
                const mlsKnownPrice = !isManual
                  && listing?.price != null
                  && listing?.price !== '';
                if (mlsKnownPrice) {
                  return (
                    <>
                      <div style={{
                        ...ORM.input,
                        background: '#f3f4f6',
                        color: '#111827',
                        fontWeight: 700,
                        cursor: 'default',
                      }}>
                        ${Number(listing.price).toLocaleString()}
                      </div>
                      <div style={ORM.hint}>List price is set by the MLS feed for this property. Doorly will negotiate the final offer price.</div>
                    </>
                  );
                }
                return (
                  <>
                    <input type="number" value={listPrice} onChange={e => setListPrice(e.target.value)} placeholder="389000" style={ORM.input} />
                    <div style={ORM.hint}>Enter the current MLS list price. Doorly will negotiate the final offer price.</div>
                  </>
                );
              })()}
              {overBudget && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', marginTop: 10, fontSize: 12.5, color: '#991b1b', lineHeight: 1.5 }}>
                  This list price (${lp.toLocaleString()}) exceeds your max approved purchase price (${Number(maxPrice).toLocaleString()}). Offers can't be submitted above your approval — contact your Doorly coordinator.
                </div>
              )}
              <label style={ORM.label}>Notes (optional)</label>
              <textarea value={propertyNotes} onChange={e => setPropertyNotes(e.target.value)} placeholder="Any details about this property..." rows={3} style={{ ...ORM.input, resize: 'vertical' }} />
              <button onClick={() => setStep(2)} disabled={!propertyAddress.trim() || !listPrice || overBudget}
                style={{ ...ORM.primaryBtn, width: '100%', marginTop: 18, background: (!propertyAddress.trim() || !listPrice || overBudget) ? '#9ca3af' : '#3a6ad9', cursor: (!propertyAddress.trim() || !listPrice || overBudget) ? 'default' : 'pointer' }}>
                Next →
              </button>
            </div>
          )}

          {/* ── Step 2 — Loan Comparison (SmartEstimate vs this home) ── */}
          {step === 2 && (
            <div>
              <p style={{
                fontSize: 14, color: 'rgba(0,0,0,0.6)',
                marginBottom: 20, lineHeight: 1.5,
              }}>
                Here is a side-by-side comparison of the terms you accepted
                on your SmartEstimate and the home you have chosen to move
                forward with.
              </p>

              {/* Selected option — full-width pill spanning both columns */}
              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 999,
                padding: '7px 14px',
                fontSize: 12,
                fontWeight: 600,
                color: '#1e40af',
                textAlign: 'center',
                marginBottom: 14,
              }}>
                {/* ── 🚨 THIS BANNER NAMED AN OPTION THAT NO LONGER EXISTS ──────
                    It keyed on the resale percentage: 5 → "Option 1 — 5% Resale
                    Adj. + 2% Program Fee", 10 → "Option 2 … $0 Program Fees".
                    A borrower on the CURRENT option carries 10% and was shown
                    "Option 2 — $0 Program Fees" while their Loan Estimate and
                    Closing Disclosure both disclose 3%.

                    🔑 IT NOW STATES THE BORROWER'S OWN FIGURES rather than
                    naming a structure. A retired file reads back exactly what it
                    signed, and there is no label to go stale the next time the
                    terms move. */}
                {(() => {
                  const resaleAdj = Number(loanDetails?.resale_adjustment);
                  const fee = Number(loanDetails?.program_fee);
                  const feePart = Number.isFinite(fee)
                    ? (fee > 0 ? ` + $${Math.round(fee).toLocaleString()} Program Fee` : ', no Program Fee')
                    : '';
                  // 🔑 (2026-10-21) No resale adjustment for a new file: the price is Doorly's contract price.
                  if (!Number.isFinite(resaleAdj) || resaleAdj <= 0) return `Doorly's contract price, no markup${feePart}`;
                  return `${resaleAdj}% Resale Adjustment${feePart}`;
                })()}
              </div>

              {/* Side-by-side grid — minmax(0,1fr) keeps both columns equal width */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: 12,
                marginBottom: 16,
              }}>
                {/* LEFT — SmartEstimate (approved terms) */}
                <div style={{ minWidth: 0 }}>
                  {/* Column header */}
                  <div style={{
                    fontSize: 11, fontWeight: 600,
                    color: '#6b7280', textTransform: 'uppercase',
                    letterSpacing: '0.06em', marginBottom: 8,
                  }}>
                    Your SmartEstimate
                  </div>

                  {/* Help card — when the borrower selected these terms */}
                  <div style={{
                    background: 'rgba(172, 244, 78, 0.18)',
                    border: '1px solid #4f7d1f',
                    borderRadius: 6,
                    padding: '7px 9px',
                    fontSize: 10,
                    color: '#4f7d1f',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    marginBottom: 10,
                    minHeight: 32,
                    boxSizing: 'border-box',
                  }}>
                    {(() => {
                      const ts = loanDetails?.smartestimate_accepted_at;
                      const when = ts
                        ? new Date(ts).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                        : null;
                      return when ? `Selected on ${when}` : 'Your approved terms';
                    })()}
                  </div>

                  {/* Left column rows */}
                  {(() => {
                    const rate = loanDetails?.approved_interest_rate;
                    // 🔑 THE ACCEPTED TERMS ARE AT THE PRICE SIGNED (fp-smartest, 2026-11-01): doorly_purchase_price, which
                    // accept-terms writes. (max_approval_amount holds the same today, but it is read as "the maximum"
                    // elsewhere; the maximum itself is decision_max_purchase_price.)
                    const signedPrice = Number(loanDetails?.doorly_purchase_price || loanDetails?.max_approval_amount || 0);
                    const downPct = loanDetails?.down_payment_pct || 10;

                    const doorlyPrice = signedPrice;
                    // Borrower Purchase Price = Doorly price + resale adjustment
                    const borrowerPrice = doorlyPrice > 0
                      ? Math.round(doorlyPrice)   // D3: no resale adjustment
                      : 0;
                    const downAmt = Math.round(borrowerPrice * (downPct / 100));
                    const loanAmt = borrowerPrice - downAmt;
                    // Full PITI — use the exact figure Atlas computed for the
                    // borrower's accepted SmartEstimate; fall back to a fresh
                    // estimate only if it isn't stored.
                    const estMonthly = loanDetails?.atlas_monthly_payment
                      ? Math.round(Number(loanDetails.atlas_monthly_payment))
                      : estFullMonthlyPayment({ price: borrowerPrice, downPct, rate });
                    // Atlas's stored atlas_cash_to_close was computed
                    // with program_fee_actual = 0 (the Option 2 baseline
                    // baked into backend/utils/atlas.js:419-432). If the
                    // borrower selected Option 1, add 2% × borrowerPrice
                    // back in so the displayed CTC reflects what they'll
                    // actually wire at contract execution. Option 2
                    // borrowers see the Atlas figure unchanged.
                    const atlasCtc = Number(
                      loanDetails?.atlas_cash_to_close ||
                      loanDetails?.cash_to_close || 0
                    );
                    // ── 🚨 THE FEE IS THE BORROWER'S OWN, NOT A RATE COMPUTED HERE ──
                    //
                    // This read `option === 1 ? price * 0.02 : 0` — the retired
                    // two-option rule — so a borrower on the CURRENT option saw
                    // a $0 program fee folded into cash-to-close on every home
                    // they looked at, while their Loan Estimate and Closing
                    // Disclosure both disclose 3%.
                    //
                    // 🔑 loanDetails.program_fee IS THE SNAPSHOT the acceptance
                    // wrote. Reading it means this screen cannot disagree with
                    // the documents, and cannot go stale the next time the rate
                    // moves. A file with no snapshot shows no fee rather than a
                    // guessed one.
                    const programFee = Number.isFinite(Number(loanDetails?.program_fee))
                      ? Number(loanDetails.program_fee)
                      : 0;
                    const cashToClose = atlasCtc + programFee;

                    const fmt$ = v => v
                      ? `$${Number(v).toLocaleString()}`
                      : '—';
                    const fmtPct = v => v != null
                      ? `${Number(v).toFixed(2)}%`
                      : '—';

                    return [
                      // The Doorly price row only where it differs (a file signed under a retired option).
                      ['Your Est. Purchase Price', fmt$(borrowerPrice)],
                      ['Down Payment', `${fmt$(downAmt)} (${downPct}%)`],
                      ['Loan Amount', fmt$(loanAmt)],
                      ['Interest Rate', fmtPct(rate)],
                      ['Est. Monthly Payment',
                        estMonthly ? `${fmt$(estMonthly)}/mo` : '—'],
                      ['Est. Cash to Close',
                        cashToClose ? fmt$(cashToClose) : '—'],
                    ].map(([label, value], i) => (
                      <div key={label} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px 10px',
                        background: i % 2 === 0
                          ? '#f9fafb' : '#ffffff',
                        borderBottom: '1px solid #f3f4f6',
                        fontSize: 12,
                      }}>
                        <span style={{
                          color: '#9ca3af',
                          fontSize: 10,
                          marginBottom: 2,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}>
                          {label}
                        </span>
                        <span style={{
                          fontWeight: 600,
                          color: '#111827',
                        }}>
                          {value}
                        </span>
                      </div>
                    ));
                  })()}
                </div>

                {/* RIGHT — Updated estimate based on this home */}
                <div style={{ minWidth: 0 }}>
                  {/* Column header */}
                  <div style={{
                    fontSize: 11, fontWeight: 600,
                    color: '#6b7280', textTransform: 'uppercase',
                    letterSpacing: '0.06em', marginBottom: 8,
                  }}>
                    This Home
                  </div>

                  {/* Help card — identical formatting to the left column */}
                  <div style={{
                    background: 'rgba(172, 244, 78, 0.18)',
                    border: '1px solid #4f7d1f',
                    borderRadius: 6,
                    padding: '7px 9px',
                    fontSize: 10,
                    color: '#4f7d1f',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    marginBottom: 10,
                    minHeight: 32,
                    boxSizing: 'border-box',
                  }}>
                    Based on current list price
                  </div>

                  {/* Right column rows */}
                  {(() => {
                    const rate = loanDetails?.approved_interest_rate;
                    const downPct = loanDetails?.down_payment_pct || 10;
                    const lp = Number(listPrice) || 0;

                    // Doorly price = list price
                    // Borrower price = list price + resale adjustment
                    const borrowerPrice = lp > 0
                      ? Math.round(lp)   // D3: no resale adjustment
                      : 0;
                    const downAmt = Math.round(borrowerPrice * (downPct / 100));
                    const loanAmt = borrowerPrice - downAmt;
                    // Full PITI estimate for this home's price, using the
                    // same assumptions as Atlas (tax 1.8%, ins 0.5%; no PMI).
                    const estMonthly = estFullMonthlyPayment({ price: borrowerPrice, downPct, rate });
                    // Closing costs ≈ 5.5% of borrower purchase price.
                    // Matches Atlas's closingCostPct constant
                    // (backend/utils/atlas.js:238) which folds lender
                    // closing + 3rd party + prepaid HOI/tax/interest +
                    // initial escrow into one figure. The prior 2.5%
                    // of loan amount only covered raw lender closing
                    // and undershot the borrower's wire by $4-9k.
                    const estClosing = Math.round(borrowerPrice * 0.055);
                    // Program fee — a percentage of the loan amount, from
                    // the file's own snapshot. Matches the cdMath/Atlas
                    // program_fee_actual convention so the "This Home"
                    // CTC mirrors the borrower's true wire amount.
                    // ── 🚨 THE FEE IS THE BORROWER'S OWN, NOT A RATE COMPUTED HERE ──
                    //
                    // This read `option === 1 ? price * 0.02 : 0` — the retired
                    // two-option rule — so a borrower on the CURRENT option saw
                    // a $0 program fee folded into cash-to-close on every home
                    // they looked at, while their Loan Estimate and Closing
                    // Disclosure both disclose 3%.
                    //
                    // 🔑 loanDetails.program_fee IS THE SNAPSHOT the acceptance
                    // wrote. Reading it means this screen cannot disagree with
                    // the documents, and cannot go stale the next time the rate
                    // moves. A file with no snapshot shows no fee rather than a
                    // guessed one.
                    const programFee = Number.isFinite(Number(loanDetails?.program_fee))
                      ? Number(loanDetails.program_fee)
                      : 0;
                    const cashToClose = downAmt + estClosing + programFee;

                    const fmt$ = v => v
                      ? `$${Number(v).toLocaleString()}`
                      : '—';
                    const fmtPct = v => v != null
                      ? `${Number(v).toFixed(2)}%`
                      : '—';

                    return [
                      ['Your Est. Purchase Price',
                        borrowerPrice ? fmt$(borrowerPrice) : '—'],
                      ['Down Payment',
                        downAmt ? `${fmt$(downAmt)} (${downPct}%)` : '—'],
                      ['Loan Amount', loanAmt ? fmt$(loanAmt) : '—'],
                      ['Interest Rate', fmtPct(rate)],
                      ['Est. Monthly Payment',
                        estMonthly ? `${fmt$(estMonthly)}/mo` : '—'],
                      ['Est. Cash to Close',
                        cashToClose ? fmt$(cashToClose) : '—'],
                    ].map(([label, value], i) => (
                      <div key={label} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px 10px',
                        background: i % 2 === 0
                          ? '#f9fafb' : '#ffffff',
                        borderBottom: '1px solid #f3f4f6',
                        fontSize: 12,
                      }}>
                        <span style={{
                          color: '#9ca3af',
                          fontSize: 10,
                          marginBottom: 2,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}>
                          {label}
                        </span>
                        <span style={{
                          fontWeight: 600,
                          color: '#3a6ad9',
                        }}>
                          {value}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Disclaimer card — no emoji */}
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '12px 14px',
                fontSize: 12,
                color: '#6b7280',
                lineHeight: 1.6,
                marginBottom: 16,
              }}>
                These are estimates only. Final figures are confirmed
                at contract execution after Doorly negotiates the
                accepted offer price.
              </div>

              {/* Navigation */}
              <div style={ORM.navRow}>
                <button onClick={() => setStep(1)} style={ORM.backBtn}>Back</button>
                <button onClick={() => setStep(3)} style={ORM.primaryBtn}>Next</button>
              </div>
            </div>
          )}

          {/* ── Step 3 — Confirm & pay ── */}
          {step === 3 && (
            <div>
              <div style={ORM.stepHeader}>Confirm and submit</div>
              <p style={ORM.sub}>Please read and confirm each item before submitting your offer request.</p>
              {[
                { key: 'negotiation', text: 'I understand that Doorly will negotiate on the price of this home and the final purchase price may differ from the list price. I authorize Doorly to submit an offer at their discretion.' },
                { key: 'downPayment', text: `I understand that my down payment of approximately $${downAmt.toLocaleString()} is due at contract execution. Funds must be available and ready to wire at that time.` },
                { key: 'closingFunds', text: 'I understand that remaining closing funds (estimated closing costs) are due 3 business days prior to closing. I will receive wire instructions from Doorly via my Dashboard at that time.' },
                { key: 'paymentEstimate', text: 'I understand that monthly payment and closing cost figures shown are estimates based on the list price. Final amounts will be confirmed in my Loan Estimate after an offer is accepted.' },
                { key: 'addressConfirm', text: `I confirm that ${propertyAddress} is the correct property address and I am ready to proceed with an offer request on this home.` },
              ].map(({ key, text }) => (
                <label key={key} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', marginBottom: 8,
                  background: acks[key] ? '#f0fdf4' : '#f9fafb',
                  border: `1px solid ${acks[key] ? '#bbf7d0' : '#e5e7eb'}`,
                  borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#374151', lineHeight: 1.5,
                }}>
                  <input type="checkbox" checked={acks[key]} onChange={e => setAcks(prev => ({ ...prev, [key]: e.target.checked }))} style={{ marginTop: 2, cursor: 'pointer' }} />
                  <span>{text}</span>
                </label>
              ))}

              {!feeWaived && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 14px', marginTop: 12, marginBottom: 16, fontSize: 13, color: '#1e40af' }}>
                  <strong>${Math.max(500, Math.round(lp * 0.02)).toLocaleString()} Purchase Initiation Deposit</strong> (2% of list price)<br />
                  Collected via bank transfer (ACH) when you submit your offer, and applied as a lender credit at closing. You'll complete payment after confirming above.
                </div>
              )}
              {feeWaived && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginTop: 12, marginBottom: 16, fontSize: 13, color: '#166534' }}>
                  ✓ Your purchase initiation deposit has been waived.
                </div>
              )}

              <div style={ORM.navRow}>
                <button onClick={() => setStep(2)} style={ORM.backBtn}>← Back</button>
                <button disabled={!allAcked}
                  onClick={() => { const p = buildPayload(); if (feeWaived) onWaivedSubmit?.(p); else onPaymentRequest?.(p); }}
                  style={{ ...ORM.primaryBtn, background: !allAcked ? '#9ca3af' : '#3a6ad9', cursor: !allAcked ? 'default' : 'pointer' }}>
                  {feeWaived
                    ? 'Submit Offer Request →'
                    : `Pay $${Math.max(500, Math.round(lp * 0.02)).toLocaleString()} & Submit →`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Recently Viewed ───────────────────────────────────────────────────────────
function RecentlyViewed({ allListings }) {
  const [ids, setIds] = useState([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      setIds(stored.slice(0, 4));
    } catch (_) {}
  }, []);

  const homes = ids.map((id) => allListings.find((l) => l.id === id)).filter(Boolean);

  if (!homes.length) return null;

  return (
    <div className="mt-10">
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Recently Viewed</h2>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {homes.map((listing) => (
          <Link
            key={listing.id}
            to={`/homes/${listing.id}`}
            className="flex-shrink-0 w-48 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
          >
            <img src={listing.photos[0]} alt={listing.address.street} className="w-full h-28 object-cover" />
            <div className="p-3">
              <p className="text-sm font-bold text-black">{fmt$(listing.price)}</p>
              <p className="text-xs text-gray-500 truncate">{listing.address.street}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Active-offer guard — 3c commit 2 — now sourced from the shared
// SHOWING_OFFER_BLOCKED_STAGES constant in constants/statusColors.js
// (lockstep mirror of backend/constants/stages.js). HomeDetail.js and
// the backend /showing + /offer routes bind to the same list, so the
// frontend hide + backend reject can never disagree on the boundary.
const ACTIVE_OFFER_STATUSES = SHOWING_OFFER_BLOCKED_STAGES;

const formatStatus = (status) => ({
  offer_requested: 'Offer Requested',
  offer_submitted: 'Offer Submitted',
  offer_accepted:  'Offer Accepted',
  option_period:   'Under Contract',
  financing:       'In Financing',
  clear_to_close:  'Clear to Close',
  closing:         'Closing',
  closed:          'Closed',
}[status] || status);

// Layer 2 → card shape adapter extracted to frontend/src/utils/listingShape.js
// (3b commit 2) so HomeDetail.js can import the same definition. One
// source, two consumers — no drift between card + detail shape.

// ── Main Homes Component ──────────────────────────────────────────────────────
const DEFAULT_FILTERS = {
  beds: null,
  baths: null,
  maxPrice: null,
  minSqft: null,
  maxSqft: null,
  minLotSize: null,
  maxLotSize: null,
  location: '',
  sort: 'newest',
};

export default function Homes() {
  const { token } = useAuth();
  const [loanDetails, setLoanDetails] = useState(null);
  // ⚠️ DECLARED WITH THE OTHER HOOKS, NOT BESIDE THE VALUE THAT USES IT. The
  // first attempt put this next to hasActiveOffer, which sits AFTER an early
  // return — a conditional hook, which parses fine and fails the build.
  // Set from /api/users/me/loan-details, which asks utils/borrowerArrival.js.
  // Defaults false so an unknown arrival shows the button rather than hiding it.
  const [referredByRealtor, setReferredByRealtor] = useState(false);
  const [loanLoading, setLoanLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [maxBuyingPower, setMaxBuyingPower] = useState(null);
  const [loading, setLoading] = useState(true);
  // Snapshot the persisted state once so the filter + AI initializers
  // read from the same object — guarantees a coherent restored view
  // (filters and AI state always come from the same prior session).
  const stickyInitial = useRef(loadStickyFilterState()).current;
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...(stickyInitial?.filters || {}),
  }));
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'saved'
  const [savedIds, setSavedIds] = useState(new Set());
  const [savedListings, setSavedListings] = useState([]);
  const [showingModal, setShowingModal] = useState(null);
  const [offerModal, setOfferModal] = useState(null);
  const [smartModal, setSmartModal] = useState(false);
  const [offerSuccess, setOfferSuccess] = useState(false);
  // showingCredits state + poll removed 3c commit 1 — the COMING SOON
  // placeholder was the only consumer; RequestShowingModal fetches its
  // own credits internally. Re-add here only when a Homes-level
  // surface (e.g., a header chip) needs the live count.
  const [showOfferPayment, setShowOfferPayment] = useState(false);

  // AI search state. aiQuery is the live input; aiParsed holds the
  // filters Anthropic extracted on the last submit; aiUnmapped is the
  // amenity terms we couldn't filter on (surfaced as a transparent
  // "ignored" pill so the borrower knows the search wasn't silently
  // dropping context). aiLoading gates the submit button. When
  // aiActive is true, the AI's result list overrides the regular
  // filter-driven fetchListings output until the borrower clears.
  //
  // Initial values are hydrated from sessionStorage so the borrower's
  // last AI search survives navigation to a listing detail and back
  // (sticky filters). The re-run of the AI search itself happens in a
  // mount-once effect below.
  const [aiQuery, setAiQuery] = useState(stickyInitial?.aiQuery || '');
  const [aiParsed, setAiParsed] = useState(stickyInitial?.aiParsed || null);
  const [aiUnmapped, setAiUnmapped] = useState(Array.isArray(stickyInitial?.aiUnmapped) ? stickyInitial.aiUnmapped : []);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiActive, setAiActive] = useState(!!stickyInitial?.aiActive);
  const [aiError, setAiError] = useState(null);
  // Normalized offer payload built by OfferRequestModule (Step 4). Both the
  // MLS-listing and manual-entry flows produce the same shape, so the
  // PaymentModal below has a single source to read from.
  const [pendingOffer, setPendingOffer] = useState(null);
  const navigate = useNavigate();

  // Manual property submission state
  const [submitPropertyModal, setSubmitPropertyModal] = useState(false);
  const [submitPropertyForm, setSubmitPropertyForm] = useState({ address: '', city: '', state: 'TX', zip: '', listing_url: '', list_price: '', request_type: 'showing', notes: '', preferred_date: '', preferred_time: '', time_unknown: false });
  const [submitPropertyLoading, setSubmitPropertyLoading] = useState(false);
  const [submitPropertySuccess, setSubmitPropertySuccess] = useState(false);
  const [submitPropertyError, setSubmitPropertyError] = useState(null);

  // Layer 3a — Layer 2 listings state. Distinct from `listings` (which
  // still carries the MOCK-shape on first paint) so the response's
  // ineligible/ceiling fields can drive their own UI states.
  const [layer2Response, setLayer2Response] = useState(null);
  // MLS attribution — required compliance footer on the Homes page.
  // Loaded once per session via GET /api/homes/mls-attribution (cached
  // 24h server-side). Empty array → render no footer (graceful
  // degradation if the upstream attribution endpoint is down).
  const [mlsProfiles, setMlsProfiles] = useState([]);

  // ── Fetch loan details (access control) ────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/users/me/loan-details`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setLoanDetails(r.data); setReferredByRealtor(!!r.data?.referred_by_realtor); })
      .catch(() => setLoanDetails(null))
      .finally(() => setLoanLoading(false));
  }, [token]);

  // ── Fetch showing credits (poll every 30s to catch admin confirmations) ─────
  // (showing-credits polling removed; see comment by state declaration)

  // ── Fetch listings ──────────────────────────────────────────────────────────
  // Layer 3a — wired to GET /api/homes/listings (Layer 2's Doorly-aware
  // wrapper around the AnyProp HAR feed). Returns Layer 2 shape:
  //   { ineligible, reason, ceiling, ceilingSource, subtypes,
  //     region, listings:[Layer2 listing objects], borrowerId }
  // We adapt the Layer 2 listings to the existing card shape via
  // mapL2ToCardShape so the ListingCard JSX stays unchanged; the new
  // overlay fields (smartEstimate, uwAnnotation, affordability) ride
  // through on the same object as additive extras.
  //
  // The legacy GET /api/homes + MOCK_LISTINGS endpoint is untouched on
  // the backend — saved/showing/offer routes still reference MOCK by
  // id. That migration is 3b's scope.
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.beds) params.beds = filters.beds;
      if (filters.baths) params.baths = filters.baths;
      if (filters.maxPrice) params.max_price = filters.maxPrice;
      if (filters.sort) params.sort = filters.sort;
      // top / skip — Layer 2 has cursor pagination via $top/$skip.
      // Default 30 surfaces enough cards for an initial grid without
      // overpaging.
      params.top = 30;

      const res = await axios.get(`${API_URL}/api/homes/listings`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setLayer2Response(res.data || null);
      const rawL2 = Array.isArray(res.data?.listings) ? res.data.listings : [];
      const mapped = rawL2.map(mapL2ToCardShape).filter(Boolean);
      setListings(mapped);
      if (!allListings.length) setAllListings(mapped);
      if (res.data?.ceiling && !maxBuyingPower) setMaxBuyingPower(Number(res.data.ceiling));
    } catch (_) {
      setListings([]);
      setLayer2Response(null);
    } finally {
      setLoading(false);
    }
  }, [filters, token]); // eslint-disable-line

  // Skip the broad fetch when an AI search is active (restored or fresh) —
  // the AI-search effect/handlers own the listings array in that mode.
  // Without this gate, on remount with aiActive=true the broad fetch
  // would race the AI re-run and the cards could flicker.
  useEffect(() => {
    if (aiActive) return;
    fetchListings();
  }, [fetchListings, aiActive]);

  // Re-run the last AI search on mount when sessionStorage restored an
  // active AI query — keeps the grid showing the same filtered results
  // the borrower left when they clicked into a listing detail. Runs
  // once on mount; subsequent searches go through runAiSearch directly.
  const aiRestoredRef = useRef(false);
  useEffect(() => {
    if (aiRestoredRef.current) return;
    aiRestoredRef.current = true;
    if (aiActive && aiQuery) {
      runAiSearch(aiQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist sticky state on changes so a subsequent mount can restore.
  // aiQuery is included so the search bar text comes back populated;
  // it does mean keystrokes write to sessionStorage, which is cheap
  // (synchronous, kilobytes-of-payload).
  useEffect(() => {
    saveStickyFilterState({ aiQuery, aiParsed, aiUnmapped, aiActive, filters });
  }, [aiQuery, aiParsed, aiUnmapped, aiActive, filters]);

  // ── Fetch MLS attribution (once per session — backend caches 24h) ──────
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/homes/mls-attribution`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMlsProfiles(Array.isArray(r.data?.profiles) ? r.data.profiles : []))
      .catch(() => setMlsProfiles([]));
  }, [token]);

  // ── Fetch saved ─────────────────────────────────────────────────────────────
  // saved_homes.listing_data is the raw Layer 2 normalized shape from
  // getListingForBorrower (address.full, livingArea, photos: [{url}]),
  // NOT the card shape ListingCard expects (address.street, sqft,
  // photos: [url]). Without the adapter the Saved tab crashed on
  // `listing.sqft.toLocaleString()` because sqft is undefined on Layer
  // 2 objects. Run every row through mapL2ToCardShape so the Saved
  // grid renders with the same card shape the All Homes grid uses.
  const fetchSaved = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/homes/saved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const savedData = res.data.saved || [];
      setSavedListings(
        savedData
          .map((s) => mapL2ToCardShape(s.listing_data))
          .filter(Boolean)
      );
      setSavedIds(new Set(savedData.map((s) => String(s.listing_id))));
    } catch (_) {}
  }, [token]);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  // Also fetch all listings (no filters) for recently-viewed lookup.
  // Repointed to /api/homes/listings (Layer 2) — the legacy /api/homes
  // route was deleted in 3b commit 062679e along with MOCK_LISTINGS,
  // leaving this caller 404ing. Layer 2 still returns { listings: [...] }
  // so the .data.listings access works unchanged.
  useEffect(() => {
    if (allListings.length) return;
    axios.get(`${API_URL}/api/homes/listings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAllListings((r.data.listings || []).map(mapL2ToCardShape).filter(Boolean)))
      .catch(() => {});
  }, [token]); // eslint-disable-line

  // ── Toggle save ─────────────────────────────────────────────────────────────
  const toggleSave = async (listing) => {
    const id = String(listing.id);
    if (savedIds.has(id)) {
      setSavedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      setSavedListings((prev) => prev.filter((l) => String(l.id) !== id));
      try {
        await axios.delete(`${API_URL}/api/homes/save/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (_) {}
    } else {
      setSavedIds((prev) => new Set([...prev, id]));
      setSavedListings((prev) => [...prev, listing]);
      try {
        await axios.post(`${API_URL}/api/homes/save`, { listing_id: id }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (_) {}
    }
  };

  // Resets BOTH the regular filter state and the AI-search state.
  // The AI search overwrites the listings array directly (via the
  // /ai-search endpoint response), so without clearing aiActive +
  // the parsed-filter UI, Reset Filters did nothing when the empty
  // state was caused by an AI search that returned 0 rows — user
  // was stuck on "No Homes Found." Same handler is passed to
  // SmartModal (lines ~278/305/333) so its "View all listings"
  // CTAs benefit too.
  const resetFilters = () => {
    setAiQuery('');
    setAiParsed(null);
    setAiUnmapped([]);
    setAiActive(false);
    setAiError(null);
    setFilters(DEFAULT_FILTERS);
    // Explicit refetch — setFilters(DEFAULT_FILTERS) is a no-op when
    // filters were already at DEFAULT (same module-scope reference),
    // so the [filters] useEffect wouldn't fire on its own. The
    // explicit call ensures the broad listing set replaces the
    // AI-filtered (possibly empty) one immediately. In the dirty-
    // filter case this causes a second fetch on the next render
    // (filters change → useCallback re-creates → useEffect fires);
    // last write wins, so user-visible result is correct.
    fetchListings();
  };

  // AI search — POST the raw query to /api/homes/ai-search. The
  // endpoint parses with Anthropic Haiku, calls the same Layer 2
  // searchForBorrower, and returns the SAME response shape as
  // GET /listings plus { parsedFilters, unmappedKeywords }. We
  // overwrite listings + layer2Response directly so the existing
  // grid render path picks it up; aiActive flips the surface state
  // (parsed-tag bar visible, "Show all" reset button shown). The
  // borrower's ceiling is still enforced server-side — Layer 2 caps
  // any AI-returned maxPrice at the atlas-derived ceiling.
  const runAiSearch = async (rawQuery) => {
    const q = String(rawQuery || '').trim();
    if (!q) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/homes/ai-search`,
        { query: q },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLayer2Response(res.data || null);
      const rawL2 = Array.isArray(res.data?.listings) ? res.data.listings : [];
      setListings(rawL2.map(mapL2ToCardShape).filter(Boolean));
      setAiParsed(res.data?.parsedFilters || {});
      setAiUnmapped(Array.isArray(res.data?.unmappedKeywords) ? res.data.unmappedKeywords : []);
      setAiActive(true);
    } catch (e) {
      setAiError("Couldn't run that search — try simpler wording or use the regular filters.");
      setListings([]);
    } finally {
      setAiLoading(false);
      // Also clear the grid's `loading` flag — the [filters] useEffect's
      // fetchListings is gated when aiActive=true (see the mount effects
      // above), so when sessionStorage restores aiActive=true and the
      // mount-once effect re-runs the AI search, fetchListings never
      // fires to flip loading→false on its own. Without this the grid
      // stays in its skeleton/grey state forever after Dashboard → Homes
      // navigation. Harmless no-op when called from a fresh AI search
      // click (loading was already false).
      setLoading(false);
    }
  };

  const clearAiSearch = () => {
    setAiQuery('');
    setAiParsed(null);
    setAiUnmapped([]);
    setAiActive(false);
    setAiError(null);
    // Re-fetch the default listing set so the grid refills immediately
    // without waiting for a filter-state mutation.
    fetchListings();
  };

  // Remove a single parsed filter pill — re-run the AI search with
  // the remaining filters by reconstructing a minimal English query.
  // (Simpler than synthesizing a partial AI response; keeps one
  // source of truth: every AI-driven listing render comes from the
  // /ai-search endpoint.)
  const removeAiFilter = async (key) => {
    if (!aiParsed) return;
    const next = { ...aiParsed };
    delete next[key];
    setAiParsed(next);
    // If no filters left, fall back to broad search.
    if (Object.keys(next).length === 0) {
      clearAiSearch();
      return;
    }
    // Re-call the endpoint with the simplified query so the same
    // Layer 2 path runs and the response shape stays consistent.
    // street goes FIRST so the address fast-path regex still triggers
    // (the regex only matches when the query starts with digits + a
    // word) — otherwise removing some other tag could downgrade an
    // address search into a broad filter search.
    const parts = [];
    if (next.street)   parts.push(next.street);
    if (next.beds)     parts.push(`${next.beds} bed`);
    if (next.baths)    parts.push(`${next.baths} bath`);
    if (next.maxPrice) parts.push(`under $${Number(next.maxPrice).toLocaleString()}`);
    if (next.minPrice) parts.push(`over $${Number(next.minPrice).toLocaleString()}`);
    if (next.minSqft)  parts.push(`at least ${Number(next.minSqft).toLocaleString()} sqft`);
    if (next.maxSqft)  parts.push(`under ${Number(next.maxSqft).toLocaleString()} sqft`);
    if (next.city)     parts.push(`in ${next.city}`);
    if (next.sort === 'price_asc')  parts.push('cheapest first');
    if (next.sort === 'price_desc') parts.push('most expensive first');
    await runAiSearch(parts.join(' '));
  };

  const handleSubmitProperty = async () => {
    if (!submitPropertyForm.address || !submitPropertyForm.city || !submitPropertyForm.zip) {
      setSubmitPropertyError('Please fill in the street address, city, and zip code.');
      return;
    }
    // List price is required for offers — anchors every downstream
    // step (contract generation, over-budget gate, admin desk
    // triage). Showings stay optional because price is less
    // load-bearing when the borrower is just touring.
    if (
      submitPropertyForm.request_type === 'offer' &&
      !(parseFloat(submitPropertyForm.list_price) > 0)
    ) {
      setSubmitPropertyError('Please enter the home list price — it is required for offer requests.');
      return;
    }
    // Over-budget guard. Showings stay unblocked (price might be
    // negotiable, borrower may still want to tour) — just a warning
    // surfaces below the price input. Offers DO block: we won't
    // generate a contract for a home above the borrower's max
    // approval. Inline warning lives in the modal JSX.
    const maxApproved  = Number(displayedMaxPrice || 0);
    const enteredPrice = parseFloat(submitPropertyForm.list_price || 0);
    if (
      submitPropertyForm.request_type === 'offer' &&
      maxApproved > 0 && enteredPrice > maxApproved
    ) {
      setSubmitPropertyError(
        `This home's list price ($${enteredPrice.toLocaleString()}) exceeds your max approved purchase price ($${maxApproved.toLocaleString()}). ` +
        `Offers cannot be submitted on homes above your approval — contact your Doorly coordinator for assistance.`
      );
      return;
    }
    setSubmitPropertyLoading(true);
    setSubmitPropertyError(null);
    try {
      const fullAddress = buildFullAddress(
        submitPropertyForm.address,
        submitPropertyForm.city,
        submitPropertyForm.state,
        submitPropertyForm.zip
      );
      const listPriceNum = submitPropertyForm.list_price ? Number(submitPropertyForm.list_price) : null;

      // Offer path. With a fee waiver in place skip the PaymentModal and
      // POST directly — the backend re-checks server-side and consumes
      // the waiver atomically with the INSERT, so a stale frontend can't
      // double-redeem. Two waiver triggers:
      //   - offer_fee_waived: admin-granted one-use waiver
      //   - offer_initiation_fee_paid: borrower has paid the $500 fee
      //     on a prior offer request. The fee is once-per-borrower for
      //     the life of the file, so any subsequent offer auto-waives.
      // Without either, fall through to the standard Stripe flow
      // (PaymentModal → /api/homes/offer).
      if (submitPropertyForm.request_type === 'offer') {
        // Route manual offers through the OfferRequestModule wizard — same
        // experience as listing-driven offers. Prefill Step 1 from the
        // details just entered; the wizard owns the loan-terms recap, the
        // acknowledgements, and the fee/waiver decision at Step 4.
        setOfferModal({
          id: 'manual_' + Date.now(),
          manual: true,
          address: { street: fullAddress, city: '', state: '', zip: '' },
          price: listPriceNum,
          listing_url: submitPropertyForm.listing_url || null,
          notes: submitPropertyForm.notes || null,
        });
        setSubmitPropertyForm({ address: '', city: '', state: 'TX', zip: '', listing_url: '', list_price: '', request_type: 'showing', notes: '', preferred_date: '', preferred_time: '', time_unknown: false });
        setSubmitPropertyModal(false);
        return;
      }

      // ⚠️ THE TIME IS OPTIONAL AND ALL-OR-NOTHING. time_unknown suppresses it
      // outright; otherwise both halves must be present, because the server
      // validates them together against the same grid the Showami modal uses.
      const hasTime = !submitPropertyForm.time_unknown
        && !!submitPropertyForm.preferred_date && !!submitPropertyForm.preferred_time;

      // Showing path is unchanged — no payment required, and price is
      // not a hard block (borrower can still tour homes above their
      // approval; price may be negotiable). list_price flows through
      // so AdminShowingRequests can flag over-budget showings.
      const res = await axios.post(`${API_URL}/api/homes/showing`, {
        property_address: fullAddress,
        address: fullAddress,
        listing_url: submitPropertyForm.listing_url || null,
        list_price: listPriceNum,
        notes: submitPropertyForm.notes || null,
        // 🔑 THE INSTANT IS COMPUTED HERE, where the borrower's timezone is
        // actually known. Render runs UTC, so a server-side rebuild of
        // "2026-05-09" + "15:00" would shift every lead-time check by the
        // offset — which is why showing_at is a TIMESTAMPTZ written from this
        // rather than two strings reassembled later.
        //
        // ⚠️ ALL THREE GO OR NONE DO. A date with no time, or a time with no
        // instant, is the half-specified state the server now refuses outright.
        ...(hasTime ? {
          preferred_date: submitPropertyForm.preferred_date,
          preferred_time: submitPropertyForm.preferred_time,
          requested_at_iso: combineDateAndTime(
            submitPropertyForm.preferred_date, submitPropertyForm.preferred_time,
          )?.toISOString() || null,
        } : {}),
        manual_entry: true,
        source: 'borrower_manual',
        listing_id: 'manual_' + Date.now(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        setSubmitPropertySuccess(true);
        setSubmitPropertyForm({ address: '', city: '', state: 'TX', zip: '', listing_url: '', list_price: '', request_type: 'showing', notes: '', preferred_date: '', preferred_time: '', time_unknown: false });
        setTimeout(() => { setSubmitPropertySuccess(false); setSubmitPropertyModal(false); }, 3000);
      }
    } catch (err) {
      setSubmitPropertyError(err.response?.data?.error || err.message || 'Submission failed');
    } finally { setSubmitPropertyLoading(false); }
  };


  // 3c commit 5: priority reversed to prefer Layer 2's ceiling
  // (maxBuyingPower) first, then loanDetails.max_approval_amount.
  //
  // Layer 2's searchForBorrower already runs the canonical fallback
  // chain server-side (atlas_max_purchase_price → max_approval_amount
  // → est_purchase_price → form_data.purchase_price) and returns the
  // result on response.ceiling, which becomes maxBuyingPower.
  // Preferring loanDetails.max_approval_amount over that would
  // override a higher Atlas ceiling whenever both are set.
  //
  // loanDetails fallback remains for first-paint (Layer 2 fetch
  // hasn't completed yet) — once it lands, maxBuyingPower wins.
  // SmartModal ('Can't find a property?') reads this via its
  // maxBuyingPower prop; AddressChecker funnels off-MLS addresses
  // through onOfferRequest unchanged.
  // 🚨 fp-smartest (2026-11-01): no max_approval_amount fallback — after signing it is the SIGNED price, not the maximum.
  const displayedMaxPrice = loanDetails?.decision_max_purchase_price || maxBuyingPower || null;
  const displayedListings = activeTab === 'saved' ? savedListings : listings;
  const filteredDisplayListings = displayedListings.filter(l => {
    if (filters.minLotSize && l.lot_size && parseFloat(l.lot_size) < parseFloat(filters.minLotSize)) return false;
    if (filters.maxLotSize && l.lot_size && parseFloat(l.lot_size) > parseFloat(filters.maxLotSize)) return false;
    return true;
  });

  // ── Access control ─────────────────────────────────────────────────────────
  if (loanLoading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-[#3a6ad9] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  // Unlock for any borrower who has reached approval (or beyond), or who
  // accepted their SmartEstimate™ directly. Inverted from an allow-list of
  // post-approval stages to a deny-list of pre-approval stages so any
  // newly-introduced post-approval stage (CTC, funded, etc.) inherits the
  // unlocked default and doesn't get accidentally locked.
  // HOMES_LOCKED_STATUSES now imported from
  // components/HomesLockedPage so HomeDetail uses the same list.
  const stage = loanDetails?.application_status;
  // Active-offer borrower: hide the action buttons + show the banner /
  // Your Current Home card at the top of the page. activeOfferAddress
  // is sourced from offer_requests by /api/users/me/loan-details.
  const hasActiveOffer = ACTIVE_OFFER_STATUSES.includes(stage);
  const activeOfferAddress = loanDetails?.active_offer_address || null;
  const isHomesUnlocked = loanDetails?.smartestimate_accepted
    || (stage && !HOMES_LOCKED_STATUSES.includes(stage));
  if (!isHomesUnlocked) {
    return <HomesLockedPage />;
  }

  return (
    <AppShell>
      {/* Offer success toast */}
      {offerSuccess && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#acf44e', color: '#1a1a1a', padding: '12px 24px',
          borderRadius: '50px', fontSize: '14px', fontWeight: '700',
          zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', whiteSpace: 'nowrap',
        }}>
          ✓ Offer request sent! Taking you to your dashboard…
        </div>
      )}

      {/* Active-offer banner — shown for the entire offer_submitted →
          closed window so the borrower can't accidentally start a parallel
          showing/offer flow while their current deal is in motion. */}
      {hasActiveOffer && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '10px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <span style={{ fontSize: '20px' }}>🏠</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: '#1d4ed8', marginBottom: '4px' }}>
              {stage === 'closed'
                ? 'Your home purchase is complete'
                : (stage === 'closing' || stage === 'clear_to_close')
                  ? 'You have an active contract in progress'
                  : stage === 'offer_requested'
                    ? 'Your offer request is being prepared'
                    : 'You have an active offer in progress'}
            </div>
            <div style={{ fontSize: '13px', color: '#374151' }}>
              {stage === 'closed'
                ? (activeOfferAddress
                    ? `Congratulations — your purchase of ${activeOfferAddress} has been completed.`
                    : 'Congratulations — your home purchase has been completed.')
                : (stage === 'closing' || stage === 'clear_to_close')
                  ? (activeOfferAddress
                      ? `Your contract on ${activeOfferAddress} is currently being processed.`
                      : 'Your contract is currently being processed.')
                  : stage === 'offer_requested'
                    ? (activeOfferAddress
                        ? `Your offer request for ${activeOfferAddress} is being prepared by Doorly. You can't submit another offer while this one is in progress.`
                        : "Your offer request is being prepared by Doorly. You can't submit another offer while this one is in progress.")
                    : (activeOfferAddress
                        ? `Your offer on ${activeOfferAddress} is currently being processed.`
                        : 'Your offer is currently being processed.')}
              {stage !== 'offer_requested' && (
                <> You can view your loan on your{' '}
                <a href="/dashboard" style={{ color: '#1d4ed8', fontWeight: 600 }}>dashboard</a>.</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Your Current Home card — only when we have an address from
          offer_requests. Mirrors a ListingCard shell but replaces the
          action buttons with a status badge + dashboard link. */}
      {hasActiveOffer && activeOfferAddress && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            fontSize: '13px', fontWeight: 600, color: '#6b7280',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            marginBottom: '10px',
          }}>
            Your Current Home
          </div>
          <div style={{
            border: '2px solid #3a6ad9',
            borderRadius: '12px',
            padding: '16px',
            background: 'white',
          }}>
            {/* Address + status pill share a flex row so a long address wraps
                in its own column instead of running under the absolutely-
                positioned pill (was overlapping on mobile). */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '4px' }}>
              <div style={{ fontWeight: 600, fontSize: '15px', minWidth: 0 }}>
                {activeOfferAddress}
              </div>
              <div style={{
                background: '#3a6ad9', color: 'white',
                borderRadius: '20px', padding: '4px 12px',
                fontSize: '11px', fontWeight: 700,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {formatStatus(stage)}
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              Track your progress on your dashboard
            </div>
            <a
              href="/dashboard"
              style={{
                display: 'inline-block', marginTop: '10px',
                background: '#3a6ad9', color: 'white',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              View Dashboard →
            </a>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-black">Let's find your home</h1>
            <p className="text-gray-500 mt-1">Homes matched to your approval amount and eligibility</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Buying power badge */}
            {displayedMaxPrice && (
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border" style={{ backgroundColor: '#eff3fe', borderColor: '#c7d8f8' }}>
                <svg className="w-4 h-4" style={{ color: '#3a6ad9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold" style={{ color: '#3a6ad9' }}>
                  Buying power: {fmt$(displayedMaxPrice)}
                </span>
              </div>
            )}
            {/* "Can't find a property?" — opens SmartModal, which is a
                FAQ-style expander stack (GuardrailCard entries explaining
                eligibility, budget, off-MLS submissions) plus an
                address-checker. Previously gated on !hasActiveOffer to
                steer active-offer borrowers back to the pipeline; un-
                gated because the modal content is mostly informational
                + the destructive offer-submit path inside it is already
                blocked server-side by SHOWING_OFFER_BLOCKED_STAGES. */}
            <button
              onClick={() => setSmartModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Can't find a property?
            </button>
          </div>
        </div>

        {/* Tabs + view toggle row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'all' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
            >
              All Homes
            </button>
            <button
              onClick={() => setActiveTab('saved')}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'saved' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
            >
              <svg className={`w-4 h-4 ${savedIds.size > 0 ? 'text-red-500' : 'text-gray-400'}`} fill={savedIds.size > 0 ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {savedIds.size > 0 ? `${savedIds.size} Saved` : 'Saved'}
            </button>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode === 'list' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode === 'map' ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-black'}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Map
            </button>
          </div>
        </div>
      </div>

      {/* AI search bar — natural-language input. Sits between the
          tabs row and the listings grid. The borrower types plain
          English ("3 bed under 350k near Katy"); /api/homes/ai-search
          parses via Anthropic Haiku and re-runs the same Layer 2
          searchForBorrower (borrower ceiling still enforced). Parsed
          filters render as removable tags below the input. */}
      <div className="mb-6">
        <form
          onSubmit={(e) => { e.preventDefault(); runAiSearch(aiQuery); }}
          className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white shadow-sm p-2"
        >
          <div className="pl-2 text-gray-400" aria-hidden="true">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <input
            type="text"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            placeholder="Address or filters — e.g. '123 Main St' or '3 bed under 350k near Katy'"
            className="flex-1 px-2 py-2 text-sm text-gray-800 placeholder-gray-400 bg-transparent border-0 focus:outline-none focus:ring-0"
            disabled={aiLoading}
          />
          {aiActive && (
            <button
              type="button"
              onClick={clearAiSearch}
              className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-800"
            >
              Show all
            </button>
          )}
          <button
            type="submit"
            disabled={aiLoading || !aiQuery.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: '#3a6ad9' }}
          >
            {aiLoading ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                  <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="3" />
                </svg>
                Searching…
              </>
            ) : 'Search'}
          </button>
        </form>

        {/* Parsed-filter tags + unmapped keyword note + can't-find
            CTA. Only renders when the AI search is active. */}
        {aiActive && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {aiParsed?.street && (
              <AiFilterTag label={`Address: ${aiParsed.street}`} onRemove={() => removeAiFilter('street')} />
            )}
            {aiParsed?.beds != null && (
              <AiFilterTag label={`${aiParsed.beds}+ beds`} onRemove={() => removeAiFilter('beds')} />
            )}
            {aiParsed?.baths != null && (
              <AiFilterTag label={`${aiParsed.baths}+ baths`} onRemove={() => removeAiFilter('baths')} />
            )}
            {aiParsed?.minPrice != null && (
              <AiFilterTag label={`Over ${fmt$(aiParsed.minPrice)}`} onRemove={() => removeAiFilter('minPrice')} />
            )}
            {aiParsed?.maxPrice != null && (
              <AiFilterTag label={`Under ${fmt$(aiParsed.maxPrice)}`} onRemove={() => removeAiFilter('maxPrice')} />
            )}
            {aiParsed?.minSqft != null && (
              <AiFilterTag label={`${Number(aiParsed.minSqft).toLocaleString()}+ sqft`} onRemove={() => removeAiFilter('minSqft')} />
            )}
            {aiParsed?.maxSqft != null && (
              <AiFilterTag label={`Under ${Number(aiParsed.maxSqft).toLocaleString()} sqft`} onRemove={() => removeAiFilter('maxSqft')} />
            )}
            {aiParsed?.city && (
              <AiFilterTag label={`In ${aiParsed.city}`} onRemove={() => removeAiFilter('city')} />
            )}
            {aiParsed?.sort && aiParsed.sort !== 'newest' && (
              <AiFilterTag
                label={aiParsed.sort === 'price_asc' ? 'Cheapest first' : 'Most expensive first'}
                onRemove={() => removeAiFilter('sort')}
              />
            )}
            {aiUnmapped.length > 0 && (
              <span className="text-xs text-gray-500 italic">
                Ignored: {aiUnmapped.join(', ')} (not yet filterable — listings still shown)
              </span>
            )}
            {!hasActiveOffer && (
              <button
                onClick={() => setSmartModal(true)}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Can't find it? Submit any property →
              </button>
            )}
          </div>
        )}

        {aiError && (
          <p className="mt-2 text-xs text-red-600">{aiError}</p>
        )}
      </div>

      {/* Listings — full-width (AI search sidebar removed pending live
          MLS integration; the prior flex+sidebar layout collapsed to a
          single block container so the listings grid spans the page). */}
      <div className="w-full">


          {(viewMode === 'map' ? (
            // Mobile classes (homes-map-view / -list / -pane) flip the
            // layout to stacked column + capped list height + min map
            // height under 640px. Desktop default stays side-by-side.
            <div className="homes-map-view" style={{ display: 'flex', gap: '12px', height: 'calc(100vh - 220px)', minHeight: '500px' }}>
              {/* Left: compact listing list */}
              <div className="homes-map-list" style={{ width: '320px', flexShrink: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredDisplayListings.length === 0 ? (
                  <p className="text-sm text-gray-400 p-4">No homes match your filters.</p>
                ) : filteredDisplayListings.map((listing) => (
                  <div
                    key={listing.id}
                    onClick={() => {
                      try {
                        const existing = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
                        const filtered = existing.filter((id) => id !== listing.id);
                        localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify([listing.id, ...filtered].slice(0, 10)));
                      } catch (_) {}
                      window.location.href = `/homes/${listing.id}`;
                    }}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow flex items-center gap-3"
                  >
                    <img
                      src={listing.photos[0]}
                      alt={listing.address.street}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                    />
                    <div className="min-w-0">
                      <p className="font-bold text-black text-sm">{fmt$(listing.price)}</p>
                      <p className="text-xs text-gray-600 truncate">{listing.address.street}</p>
                      <p className="text-xs text-gray-400">{listing.beds}bd · {listing.baths}ba · {listing.sqft.toLocaleString()} sqft</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Right: map — 3c commit 3. Interactive Google Maps
                  instance via loadGoogleMaps + per-listing markers
                  from Layer 2's lat/lng. Marker click opens an info
                  window with a mini-card; "View details →" SPA-
                  navigates to /homes/:listingId. Replaces the prior
                  iframe-search embed which only showed the first
                  listing's city center with no markers. */}
              <HomesMap listings={filteredDisplayListings} />
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
                  <div className="bg-gray-200" style={{ paddingBottom: '56.25%' }} />
                  <div className="p-4 space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-2/3" />
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (layer2Response?.ineligible && activeTab !== 'saved') ? (
            // Layer 2 ineligible gate — distinct from "no listings."
            // Atlas flagged the borrower as ineligible (LTV out of band,
            // FICO below floor, etc.). The Dashboard hero / LO outreach
            // is the resolution path; surface a calm explanation rather
            // than an empty-results page that implies the search itself
            // is the problem.
            <div className="text-center py-16 px-8 bg-white rounded-2xl border border-gray-100">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#fffbeb' }}>
                <svg className="w-7 h-7" fill="none" stroke="#d97706" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-800 font-bold text-base">Your pricing is being finalized</p>
              <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto leading-relaxed">
                We&rsquo;re still finalizing your SmartEstimate. Once your pricing is confirmed, your personalized home search will open up automatically.
              </p>
              <button onClick={() => navigate('/dashboard')}
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg"
                style={{ background: '#3a6ad9', color: '#fff', border: 'none' }}>
                View Dashboard →
              </button>
            </div>
          ) : filteredDisplayListings.length === 0 ? (
            // Two empty-state variants based on Layer 2's coverage
            // probe (feedHasInventoryInZips):
            //   · false → true coverage gap; render "Coming soon for
            //             your area." Budget / filter adjustments
            //             won't help — the MLS feed has nothing in
            //             these zips.
            //   · true / null → filter or budget narrowed it to 0;
            //             render the existing "adjust filters" copy.
            (layer2Response?.feedHasInventoryInZips === false && activeTab !== 'saved') ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#eff3fe' }}>
                  <svg className="w-7 h-7" fill="none" stroke="#3a6ad9" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-gray-800 font-bold text-base">Coming soon for your area</p>
                <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto leading-relaxed">
                  We don't have active listings in your target zip codes yet. Our MLS coverage is rolling out — we'll surface inventory the moment it's available where you're searching.
                </p>
                <button onClick={() => setSubmitPropertyModal(true)}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg"
                  style={{ background: '#3a6ad9', color: '#fff', border: 'none' }}>
                  Submit a property →
                </button>
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <svg className="w-12 h-12 text-gray-200 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <p className="text-gray-500 font-semibold">
                  {activeTab === 'saved'
                    ? 'No saved homes yet'
                    : (layer2Response?.ceiling
                        ? `No homes found within your approved amount of ${fmt$(layer2Response.ceiling)}`
                        : 'No homes match your filters')}
                </p>
                <p className="text-gray-400 text-sm mt-1 max-w-md mx-auto">
                  {activeTab === 'saved'
                    ? 'Tap the heart on any listing to save it.'
                    : 'Try expanding your search area or adjusting filters.'}
                </p>
                {activeTab !== 'saved' && (
                  <button onClick={resetFilters} className="mt-4 btn-secondary border border-gray-200 text-gray-600 hover:bg-gray-50">
                    Reset Filters
                  </button>
                )}
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredDisplayListings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  mlsProfiles={mlsProfiles}
                  saved={savedIds.has(String(listing.id))}
                  onToggleSave={toggleSave}
                  onShowingRequest={(l) => setShowingModal(l)}
                  referredByRealtor={referredByRealtor}
                  onOfferRequest={(l) => setOfferModal(l)}
                  hasActiveOffer={hasActiveOffer}
                />
              ))}
            </div>
          ))}
        </div>

      {/* ── Recently Viewed — unhidden 3c commit 1; MLS feed is connected ── */}
      <RecentlyViewed allListings={allListings} />

      {/* Showami showing request — credit + payment gated internally */}
      {showingModal && (
        <RequestShowingModal
          listing={typeof showingModal === 'object' ? showingModal : null}
          initialView={showingModal === 'list' ? 'list' : 'form'}
          onClose={() => setShowingModal(null)}
          token={token}
        />
      )}
      {offerModal && (
        <OfferRequestModule
          listing={offerModal}
          loanDetails={loanDetails}
          feeWaived={!!(loanDetails?.offer_fee_waived || loanDetails?.offer_initiation_fee_paid || loanDetails?.is_demo)}
          onClose={() => setOfferModal(null)}
          onPaymentRequest={(payload) => {
            setOfferModal(null);
            setPendingOffer(payload);
            setShowOfferPayment(true);
          }}
          onWaivedSubmit={async (payload) => {
            // Waiver path — skip the PaymentModal entirely. POST directly
            // to /api/homes/offer; the backend re-checks offer_fee_waived
            // server-side and consumes the waiver atomically with the
            // INSERT, so a stale frontend can't double-redeem.
            try {
              await axios.post(`${API_URL}/api/homes/offer`, {
                ...payload,
                offer_initiation_payment_method: 'waived',
              }, { headers: { Authorization: `Bearer ${token}` } });
              setOfferModal(null);
              setOfferSuccess(true);
              setTimeout(() => navigate('/dashboard'), 1500);
            } catch (err) {
              console.error('[Offer] Waived submit error:', err.response?.data || err.message);
              alert(err.response?.data?.error || 'Could not submit your offer. Please try again or contact support.');
            }
          }}
        />
      )}

      {/* Purchase Initiation Deposit Payment Modal (Stripe) */}
      <PaymentModal
        isOpen={showOfferPayment}
        onClose={() => { setShowOfferPayment(false); setPendingOffer(null); }}
        type="offer_fee"
        // Stamp the offer payload into intent metadata so the borrower can
        // resume from the dashboard banner if they abandon mid-3DS.
        extraBody={pendingOffer
          ? {
              listing_id: pendingOffer.listing_id,
              address:    pendingOffer.address,
              list_price: pendingOffer.list_price,
              notes:      pendingOffer.notes,
              listing_url: pendingOffer.listing_url,
            }
          : {}}
        onSuccess={async (paymentResult) => {
          setShowOfferPayment(false);
          // Submit the offer after payment succeeds, passing the real Stripe
          // payment_intent_id so the backend can re-verify it before creating
          // the offer record. pendingOffer is the normalized payload from
          // OfferRequestModule (covers both MLS-listing and manual flows).
          try {
            if (pendingOffer) {
              await axios.post(`${API_URL}/api/homes/offer`, {
                ...pendingOffer,
                offer_initiation_payment_method: 'stripe',
                payment_intent_id: paymentResult?.payment_intent_id,
              }, { headers: { Authorization: `Bearer ${token}` } });
            }
          } catch (err) {
            console.error('[Offer] Post-payment submit error:', err.message);
          }
          setPendingOffer(null);
          setOfferSuccess(true);
          setTimeout(() => navigate('/dashboard'), 1500);
        }}
      />

      {smartModal && (
        <SmartModal
          onClose={() => setSmartModal(false)}
          token={token}
          onOfferRequest={(l) => setOfferModal(l)}
          maxBuyingPower={displayedMaxPrice}
          resetFilters={resetFilters}
          setActiveTab={setActiveTab}
          hasActiveOffer={hasActiveOffer}
        />
      )}

      {/* ── MANUAL PROPERTY SUBMISSION MODAL ── */}
      {submitPropertyModal && (
        <div onClick={() => { setSubmitPropertyModal(false); setSubmitPropertyError(null); setSubmitPropertySuccess(false); }}
          className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} className="safe-modal-box"
            style={{ background: '#fff', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '500px', '--sm-max': '90vh', overflowY: 'auto' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: '800', color: '#111827' }}>
                  {submitPropertyForm.request_type === 'showing' ? 'Request a Showing' : 'Start Purchase'}
                </h2>
                <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                  {submitPropertyForm.request_type === 'showing' ? "Enter the property address and we'll coordinate a showing." : "Found the one? Enter the address and we'll start your offer."}
                </p>
              </div>
              <button onClick={() => { setSubmitPropertyModal(false); setSubmitPropertyError(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#9ca3af', lineHeight: 1, padding: '4px' }}>x</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {[{ value: 'showing', label: 'Request Showing' }, { value: 'offer', label: 'Start Purchase' }].map(opt => (
                <button key={opt.value} onClick={() => setSubmitPropertyForm(f => ({ ...f, request_type: opt.value }))}
                  style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: '1.5px solid', borderColor: submitPropertyForm.request_type === opt.value ? '#3a6ad9' : '#e5e7eb', background: submitPropertyForm.request_type === opt.value ? '#3a6ad9' : '#fff', color: submitPropertyForm.request_type === opt.value ? '#fff' : '#374151', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {submitPropertySuccess ? (
              <div style={{ padding: '24px', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#2d6a0a', marginBottom: '4px' }}>
                  {submitPropertyForm.request_type === 'showing' ? 'Showing request received!' : 'Purchase started!'}
                </div>
                <div style={{ fontSize: '13px', color: '#2d6a0a', opacity: 0.8 }}>Our team will be in touch shortly.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Street Address *</label>
                  <AddressAutocomplete
                    name="property_street"
                    value={submitPropertyForm.address}
                    placeholder="Start typing an address..."
                    onChange={(e) => setSubmitPropertyForm(f => ({ ...f, address: e.target.value }))}
                    onSelect={({ street, city, state, zip }) =>
                      setSubmitPropertyForm(f => ({
                        ...f,
                        address: street || f.address,
                        city:    city   || f.city,
                        // ⚠️ WAS (state === 'TX' || state === 'FL'). A Florida
                        // address autocompleted straight into a property
                        // submission, on a page whose own copy lists "Location
                        // outside of Doorly's current service areas" as a
                        // rejection reason. Derived from the licence mirror now.
                        state:   LICENSED_STATE_CODES.includes(state) ? state : f.state,
                        zip:     zip    || f.zip,
                      }))
                    }
                    className="property-addr-input"
                    autoComplete="street-address"
                  />
                  <style>{`
                    .property-addr-input {
                      width: 100%;
                      padding: 10px 12px 10px 36px;
                      border: 1px solid #d1d5db;
                      border-radius: 10px;
                      font-size: 14px;
                      font-family: inherit;
                      box-sizing: border-box;
                      outline: none;
                    }
                    .property-addr-input:focus { border-color: #3a6ad9; }
                  `}</style>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>City *</label>
                    <input type="text" placeholder="Austin" value={submitPropertyForm.city} onChange={e => setSubmitPropertyForm(f => ({ ...f, city: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>State</label>
                    <select value={submitPropertyForm.state} onChange={e => setSubmitPropertyForm(f => ({ ...f, state: e.target.value }))}
                      style={{ width: '100%', padding: '10px 8px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box' }}>
                      {/* ⚠️ THIS OFFERED FL. A borrower could explicitly select
                          Florida for a property they were asking Doorly to buy,
                          on the same page that lists "Location outside of
                          Doorly's current service areas" as a rejection reason.
                          Derived from the licence mirror, so the option list
                          follows the licence rather than a memory of it. */}
                      {LICENSED_STATE_CODES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Zip *</label>
                    <input type="text" placeholder="78701" maxLength={5} value={submitPropertyForm.zip} onChange={e => setSubmitPropertyForm(f => ({ ...f, zip: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                    Listing URL <span style={{ fontWeight: '400', textTransform: 'none', color: '#9ca3af' }}>(optional)</span>
                  </label>
                  <input type="url" placeholder="https://zillow.com/..." value={submitPropertyForm.listing_url} onChange={e => setSubmitPropertyForm(f => ({ ...f, listing_url: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
                {/* Home List Price — surfaced for both showing AND offer
                    requests. Required for offers (anchors the contract
                    + over-budget gate + admin triage); optional on
                    showings since price is less load-bearing when
                    touring. handleSubmitProperty enforces the offer
                    requirement before submit. Over-approval entries
                    on offers are blocked separately. */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                    Home List Price{' '}
                    {submitPropertyForm.request_type === 'offer' ? (
                      <span style={{ fontWeight: '400', textTransform: 'none', color: '#ff082f' }}>*</span>
                    ) : (
                      <span style={{ fontWeight: '400', textTransform: 'none', color: '#9ca3af' }}>(optional)</span>
                    )}
                  </label>
                  <input type="number" placeholder="e.g. 425000"
                    value={submitPropertyForm.list_price}
                    onChange={e => setSubmitPropertyForm(f => ({ ...f, list_price: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  {(() => {
                    const max = Number(displayedMaxPrice || 0);
                    const lp  = parseFloat(submitPropertyForm.list_price || 0);
                    if (!(max > 0 && lp > max)) return null;
                    const isOffer = submitPropertyForm.request_type === 'offer';
                    return (
                      <div style={{
                        marginTop: 6, padding: '8px 10px',
                        background: isOffer ? '#fef2f2' : '#fffbef',
                        border: `1px solid ${isOffer ? '#fca5a5' : '#ffe4a0'}`,
                        borderRadius: 8, fontSize: 12,
                        color: isOffer ? '#991b1b' : '#7a5200', lineHeight: 1.5,
                      }}>
                        ⚠️ This home's list price of ${lp.toLocaleString()} exceeds your max approved purchase price of ${max.toLocaleString()}.
                        {isOffer
                          ? ' You may not be able to purchase this home with your current approval. Contact your Doorly coordinator for assistance.'
                          : ' You can still request a showing — price may be negotiable.'}
                      </div>
                    );
                  })()}
                </div>
                {submitPropertyForm.request_type === 'showing' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Preferred Date</label>
                      {/* ⚠️ min IS THE 24-HOUR FLOOR, NOT TODAY. `today` let a
                          borrower pick a date whose every slot the server then
                          refused for advance notice — a form that accepts what the
                          API rejects. earliestValidDateStr is the same floor the
                          Showami modal uses. */}
                      <input type="date" value={submitPropertyForm.preferred_date}
                        disabled={submitPropertyForm.time_unknown}
                        onChange={e => setSubmitPropertyForm(f => ({ ...f, preferred_date: e.target.value, preferred_time: '' }))}
                        min={earliestValidDateStr()}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', background: submitPropertyForm.time_unknown ? '#f3f4f6' : '#fff' }} />
                    </div>
                    <div>
                      {/* 🚨 THIS WAS A <select> OF PERIODS — morning / afternoon /
                          evening / flexible — written straight into the same TEXT
                          column the Showami modal fills with "HH:MM". Every reader
                          parsed it as a clock time and INVENTED an hour when it was
                          not one: noon on the server, midnight in the browser, both
                          feeding a $60 credit-forfeiture rule.

                          🔑 ONE GRID NOW, IMPORTED. TIME_SLOTS is the same
                          quarter-hour list the Showami modal renders, from
                          utils/showingDateUtils.js, checked server-side by the same
                          validator. Two pickers that must agree about valid hours is
                          the mirror-drift shape, and this is the half that was
                          missing. */}
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Preferred Time</label>
                      <select value={submitPropertyForm.preferred_time}
                        disabled={submitPropertyForm.time_unknown}
                        onChange={e => setSubmitPropertyForm(f => ({ ...f, preferred_time: e.target.value }))}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', background: submitPropertyForm.time_unknown ? '#f3f4f6' : '#fff', color: submitPropertyForm.time_unknown ? '#9ca3af' : 'inherit', boxSizing: 'border-box' }}>
                        <option value="">Pick a time</option>
                        {TIME_SLOTS.filter(slot => !isTimeSlotDisabled(submitPropertyForm.preferred_date, slot.value))
                          .map(slot => <option key={slot.value} value={slot.value}>{slot.label}</option>)}
                      </select>
                    </div>
                    {/* 🔑 "NOT SURE YET" IS A REAL ANSWER AND HAS TO BE A
                        SUPPORTED ONE. A borrower who has just found a listing may
                        not know their week — and this form's first job is telling
                        Doorly about the PROPERTY. Forcing a slot to get past it
                        makes them invent one, which is the same failure this whole
                        change removes, moved from the parser to the person.

                        ⚠️ AND IT WRITES NOTHING, not a period. preferred_period is
                        history-only now; an unknown time is two NULLs and a human
                        follow-up, which is what it honestly is. */}
                    <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', fontSize: '13px', color: '#4b5563', cursor: 'pointer', lineHeight: 1.5 }}>
                      <input type="checkbox" checked={submitPropertyForm.time_unknown} style={{ marginTop: '2px' }}
                        onChange={e => setSubmitPropertyForm(f => ({ ...f, time_unknown: e.target.checked, preferred_date: '', preferred_time: '' }))} />
                      <span>I&rsquo;m not sure when yet &mdash; send the property now and we&rsquo;ll sort out a time.</span>
                    </label>
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                      Notes <span style={{ fontWeight: '400', textTransform: 'none', color: '#9ca3af' }}>(optional)</span>
                    </label>
                    <textarea placeholder="Offer price in mind, timeline, anything else..." value={submitPropertyForm.notes} onChange={e => setSubmitPropertyForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                )}
                {submitPropertyForm.request_type === 'offer'
                  // Hidden once the borrower has already paid the fee on a
                  // prior offer (offer_initiation_fee_paid is set on first
                  // successful payment in homes.js) — they already know the
                  // pricing. Also hidden when offer_fee_waived is set, since
                  // the OfferModal will surface the waiver UX instead and
                  // this banner would be misleading.
                  && !loanDetails?.offer_initiation_fee_paid
                  && !loanDetails?.offer_fee_waived && (
                  <div style={{ padding: '12px 14px', background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: '10px', fontSize: '13px', color: '#7a5200', lineHeight: '1.6' }}>
                    {(() => {
                      const _lp = parseFloat(submitPropertyForm.list_price || 0);
                      const _fee = _lp > 0 ? Math.max(500, Math.round(_lp * 0.02)) : null;
                      return (
                        <>A <strong>2% Purchase Initiation Deposit{_fee ? ` (~$${_fee.toLocaleString()})` : ''}</strong> based on the home's list price is collected via bank transfer (ACH) when you submit your offer. Applied as a lender credit at closing.</>
                      );
                    })()}
                  </div>
                )}
                {submitPropertyError && (
                  <div style={{ padding: '10px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '10px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>{submitPropertyError}</div>
                )}
                <button onClick={handleSubmitProperty} disabled={submitPropertyLoading}
                  style={{ width: '100%', padding: '13px', background: submitPropertyLoading ? '#e5e7eb' : '#3a6ad9', color: submitPropertyLoading ? '#9ca3af' : '#fff', border: 'none', borderRadius: '10px', cursor: submitPropertyLoading ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: '800' }}>
                  {submitPropertyLoading ? 'Submitting...' : submitPropertyForm.request_type === 'showing' ? 'Request Showing' : 'Start Purchase'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MLS Attribution Footer — compliance requirement. Renders one
          block per originating-system profile (today HAR; ABoR/Unlock
          will add more). Graceful no-render if the upstream is down
          (empty mlsProfiles array). Logo + display name + disclaimer
          text per profile. */}
      {mlsProfiles.length > 0 && (
        <div className="mt-12 pt-8 pb-6 border-t border-gray-100">
          {mlsProfiles.map((p, i) => (
            <div key={p.originatingSystemName || i} className="text-xs text-gray-500 max-w-3xl mx-auto px-4 mb-6 last:mb-0">
              <div className="flex items-start gap-3 mb-2">
                {p.logoUrl ? (
                  <img src={p.logoUrl} alt={p.displayName || p.originatingSystemName || 'MLS'}
                    style={{ height: 28, width: 'auto' }} />
                ) : null}
                {p.displayName ? (
                  <div className="font-semibold text-gray-700" style={{ paddingTop: p.logoUrl ? 4 : 0 }}>
                    {p.displayName}
                  </div>
                ) : null}
              </div>
              {p.disclaimer ? (
                <p className="leading-relaxed">{p.disclaimer}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
